import { useI18n, useT, type Translate } from "../../../shared/i18n/i18n";
import {
  compareGroupBucketValues,
  formatGroupBucketLabel,
  groupBucketKindForValues,
  groupBucketUnits,
  suggestNumberBucketSizes,
} from "../../../entities/board/lib/group-bucket";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndSortableList,
  DndSortableVerticalList,
  MultiContainerDnd,
  useDndContainer,
  useDndSortableItem,
  type MultiContainerDropResult,
} from "../../../shared/ui/multi-container-dnd";
import type { ColumnDef } from "@tanstack/react-table";
import {
  ArrowUpDown,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Columns3,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileText,
  Gauge,
  Filter,
  FormInput,
  GripVertical,
  History,
  LayoutGrid,
  Link,
  LayoutList,
  Lock,
  Loader2,
  MoreHorizontal,
  Plus,
  Share2,
  Settings2,
  Sigma,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { motion } from "motion/react";
import { ComponentProps, CSSProperties, FormEvent, HTMLAttributes, KeyboardEvent, ReactElement, ReactNode, Ref, WheelEvent, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { BoardItemView, BoardPurpose, BoardsView, BoardView, BoardViewConfig, BoardViewField, BoardViewType } from "../../../entities/board/model/use-boards";
import { useBoards } from "../../../entities/board/model/use-boards";
import { BoardTransitionBlockedError, type BoardTransitionAssessment } from "../../../entities/board/model/use-boards";
import type { FolderDefinition } from "../../../entities/folder/model/use-folders";
import { useFolders } from "../../../entities/folder/model/use-folders";
import { useSpaces } from "../../../entities/space/model/use-spaces";
import { useSpaceLiveEvents } from "../../../entities/space/model/use-space-live-events";
import { useSprints } from "../../../entities/sprint-planning/model/use-sprints";
import { SprintPlanningSurface } from "../../../features/sprint-planning/ui/sprint-planning-surface";
import { SprintReportSurface } from "../../../features/sprint-planning/ui/sprint-report-surface";
import { SprintScopeSwitcher } from "../../../features/sprint-planning/ui/sprint-scope-switcher";
import {
  formatReplayDuration,
  SprintReplayStatusSinceContext,
  useSprintReplay,
} from "../../../features/sprint-replay/model/use-sprint-replay";
import { SprintReplayBar } from "../../../features/sprint-replay/ui/sprint-replay-bar";
import { SprintReplayLog } from "../../../features/sprint-replay/ui/sprint-replay-log";
import { FormViewSurface } from "../../../features/form-intake/ui/form-view-surface";
import { useViewDocument } from "../../../entities/document/model/use-document";
import { DocumentEditor } from "../../../entities/document/ui/document-editor";
import type { WorkObjectFieldDefinition } from "../../../entities/work-object-field/model/use-work-object-fields";
import { formatDurationFromSeconds, parseDurationToSeconds } from "../../../entities/work-object-field/model/duration-field";
import { useWorkObjectFields } from "../../../entities/work-object-field/model/use-work-object-fields";
import { formatFieldValue, formatGroupValue, referenceLabel } from "../../../entities/work-object/model/format-field-value";
import { emptyValueLabel, fieldAppliesToType, isMultiValueField, isRelationField } from "../../../entities/work-object/model/work-object-field-drafts";
import { FieldProgressTrack, readProgressConfig, WorkObjectFieldValueEditor } from "../../../entities/work-object/ui/work-object-field-editor";
import { ViewShareLinkControls } from "../../../features/view-shares/ui/view-share-link-controls";
import { DashboardWidgets } from "../../../features/dashboards/ui/dashboard-widgets";
import { useWorkObjectAudit } from "../../../entities/work-object/model/use-work-object-audit";
import { useWorkObjectFieldValues } from "../../../entities/work-object/model/use-work-object-field-values";
import {
  type CreateWorkObjectInput,
  type UpdateWorkObjectFieldValuesInput,
  type WorkObjectListView,
  useWorkObjects,
} from "../../../entities/work-object/model/use-work-objects";
import { WorkObjectDetailDialog } from "../../../entities/work-object/ui/work-object-detail-dialog";
import { BoardStatusManagerDialog } from "../../../features/board-statuses/manage-statuses/ui/board-status-manager-dialog";
import { ApplyBoardTemplateDialog } from "../../../features/boards/apply-template/ui/apply-board-template-dialog";
import { ImportItemsDialog } from "../../../features/boards/import-items/ui/import-items-dialog";
import { BoardActionsMenu } from "../../../features/boards/board-actions/ui/board-actions-menu";
import { BoardSettingsDialog } from "../../../features/boards/manage-board/ui/board-settings-dialog";
import { ResourceAccessDialog } from "../../../features/access/manage-resource-access/ui/resource-access-dialog";
import { type PredicateBuilderState, VisualPredicateBuilder } from "../../../features/board-views/ui/visual-predicate-builder";
import { BoardTimelineView, resolveTimelineDateField, type TimelineGrouping } from "../../../features/board-views/ui/board-timeline-view";
import type { TimelineRowMetaPart } from "../../../features/board-views/ui/timeline";
import { FormulaExpressionEditor } from "../../../features/work-object-fields/manage-field/ui/formula-expression-editor";
import { CreateWorkObjectForm } from "../../../features/work-objects/create-work-object/ui/create-work-object-form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { DataTable, type DataTableGroup } from "@/components/ui/data-table";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useTRPC } from "@/shared/api/trpc";
import { EditableText } from "@/shared/ui/editable-text";

const boardViewTypes: BoardViewType[] = ["list", "kanban", "timeline", "form", "document", "dashboard"];

const boardViewMeta: Record<BoardViewType, { icon: ReactNode; labelKey: string }> = {
  dashboard: { icon: <Gauge data-icon="inline-start" />, labelKey: "board.dashboard" },
  document: { icon: <FileText data-icon="inline-start" />, labelKey: "board.document" },
  form: { icon: <FormInput data-icon="inline-start" />, labelKey: "board.form" },
  kanban: { icon: <Columns3 data-icon="inline-start" />, labelKey: "board.kanban" },
  list: { icon: <LayoutList data-icon="inline-start" />, labelKey: "board.list" },
  timeline: { icon: <CalendarDays data-icon="inline-start" />, labelKey: "board.timeline" },
};

function orderBoardsForNavigation(boards: BoardView[]) {
  return [...boards].sort((first, second) => Number(second.purpose === "default") - Number(first.purpose === "default"));
}

function orderViewsForNavigation(views: BoardView["views"]) {
  return [...views].sort((first, second) => Number(second.isDefault) - Number(first.isDefault));
}

type BoardViewFieldSummaryOperator = NonNullable<BoardViewField["summaryOperator"]>;

const summaryOperatorLabels: Record<BoardViewFieldSummaryOperator, string> = {
  avg: "Average",
  count: "Count",
  empty: "Empty",
  filled: "Filled",
  max: "Max",
  min: "Min",
  sum: "Sum",
};

const numericFieldTypes = new Set<WorkObjectFieldDefinition["type"]>([
  "currency",
  "duration",
  "formula",
  "number",
  "percent",
  "rating",
  "rollup",
]);
const singleLineEditableFieldTypes = new Set<WorkObjectFieldDefinition["type"]>([
  "currency",
  "date",
  "email",
  "number",
  "percent",
  "phone",
  "text",
  "url",
]);

type BoardsPanelProps = {
  navigationSlot?: ReactNode;
  onBoardChanged?: () => void | Promise<void>;
  // Consumed request to open one item's dialog (inbox/search deep links).
  onOpenItemRequestHandled?: () => void;
  onSelectViewChange?: (viewId: string) => void;
  openItemRequest?: { boardId: string; workObjectId: string } | null;
  selectedBoardId?: string;
  selectedViewId?: string;
  showBoardList?: boolean;
  showCreateBoardForm?: boolean;
};

// Field ids whose computed values are being recomputed (formula just saved).
// Read by computed cells to show a loader instead of a stale value.
const RecomputingFieldsContext = createContext<ReadonlySet<string>>(new Set());

export function BoardsPanel({
  navigationSlot,
  onBoardChanged,
  onOpenItemRequestHandled,
  onSelectViewChange,
  openItemRequest,
  selectedBoardId: controlledSelectedBoardId,
  selectedViewId: controlledSelectedViewId,
  showBoardList = true,
  showCreateBoardForm = true,
}: BoardsPanelProps = {}) {
  const t = useT();
  const boards = useBoards();
  const folders = useFolders();
  const spaces = useSpaces();
  const fields = useWorkObjectFields();
  const workObjects = useWorkObjects();
  const [selectedBoardItem, setSelectedBoardItem] = useState<BoardItemView | null>(null);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const selectedItemFieldValues = useWorkObjectFieldValues(selectedBoardItem?.workObject.id ?? null);
  const selectedItemAudit = useWorkObjectAudit(selectedBoardItem?.workObject.id ?? null);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [uncontrolledViewId, setUncontrolledViewId] = useState("");
  const selectedViewId = controlledSelectedViewId ?? uncontrolledViewId;
  const [isCreateBoardOpen, setIsCreateBoardOpen] = useState(false);
  const [isCreateWorkOpen, setIsCreateWorkOpen] = useState(false);
  const [isBoardSettingsOpen, setIsBoardSettingsOpen] = useState(false);
  const [isBoardPermissionsOpen, setIsBoardPermissionsOpen] = useState(false);
  const [isStatusManagerOpen, setIsStatusManagerOpen] = useState(false);
  const [isViewSettingsOpen, setIsViewSettingsOpen] = useState(false);
  const [recomputingFieldIds, setRecomputingFieldIds] = useState<ReadonlySet<string>>(() => new Set());

  const selectedBoard = useMemo(
    () =>
      boards.data?.boards.find((board) => board.id === (controlledSelectedBoardId ?? selectedBoardId)) ??
      boards.data?.boards[0] ??
      null,
    [boards.data?.boards, controlledSelectedBoardId, selectedBoardId],
  );

  const selectedView =
    selectedBoard?.views.find((view) => view.id === selectedViewId) ??
    selectedBoard?.views.find((view) => view.isDefault) ??
    selectedBoard?.views[0] ??
    null;

  const selectedBoardFields = selectedBoard
    ? fields.data.filter((field) => selectedBoard.availableFieldIds.includes(field.id))
    : fields.data;

  // Sprint boards are regular boards plus a fixed Backlog (planning) tab and
  // a sprint scope applied to every view's query.
  const isSprintBoard = selectedBoard?.kind === "sprint";
  const sprintTrack = useSprints(isSprintBoard && selectedBoard ? selectedBoard.id : null);
  const sprintTrackBoardId = selectedBoard
    ? isSprintBoard
      ? selectedBoard.id
      : boards.data?.boards.find((board) => board.kind === "sprint" && board.spaceId === selectedBoard.spaceId)?.id ?? null
    : null;
  const [sprintTab, setSprintTab] = useState<"backlog" | "report" | null>(null);
  const isBacklogTab = sprintTab !== null;
  const [sprintScope, setSprintScope] = useState<string | null>(null);
  const [sprintScopeTouched, setSprintScopeTouched] = useState(false);
  const scopeTrpc = useTRPC();
  const scopeQueryClient = useQueryClient();

  useEffect(() => {
    if (!isSprintBoard || sprintScopeTouched || !sprintTrack.data) return;
    const today = new Date().toISOString().slice(0, 10);
    const running = sprintTrack.data.sprints.find(
      (sprint) => !sprint.closedAt && sprint.startsAt <= today && sprint.endsAt >= today,
    );
    if (running) {
      setSprintScope(running.id);
    }
  }, [isSprintBoard, sprintScopeTouched, sprintTrack.data]);

  const changeSprintScope = useCallback((value: string | null) => {
    setSprintScopeTouched(true);
    setSprintScope(value);
  }, []);

  const scopedViewQuery = useQuery(
    scopeTrpc.boards.readViewQuery.queryOptions(
      isSprintBoard && sprintScope && selectedBoard && selectedView && !isBacklogTab && selectedView.type !== "timeline"
        ? { boardId: selectedBoard.id, boardViewId: selectedView.id, sprintScope }
        : skipToken,
      { placeholderData: (previous) => previous },
    ),
  );

  // While a sprint scope is active the view renders a server-scoped snapshot
  // of its query; paging within a scope is intentionally capped at the first
  // page (sprint-sized data), so cursors are dropped.
  const scopeActive = Boolean(isSprintBoard && sprintScope && !isBacklogTab && selectedView?.type !== "timeline");
  const effectiveView =
    scopeActive && selectedView && scopedViewQuery.data
      ? {
          ...selectedView,
          query: {
            groups: scopedViewQuery.data.query.groups.map((group) => ({ ...group, nextCursor: null })),
            nextCursor: null,
            workObjectIds: scopedViewQuery.data.query.workObjectIds,
          },
        }
      : selectedView;
  const effectiveBoard =
    scopeActive && selectedBoard && scopedViewQuery.data ? { ...selectedBoard, items: scopedViewQuery.data.items } : selectedBoard;

  // Replay mode: the current view renders historical frames of the selected
  // sprint. The session survives view switches and drops when the board or
  // the sprint scope changes.
  const [replaySession, setReplaySession] = useState<{ boardId: string; sprintId: string } | null>(null);
  // Deep link into a replay moment: ?replay=<sprintId>&t=<ms> on the board
  // URL opens the session and rewinds to the shared frame.
  const [pendingReplayLink, setPendingReplayLink] = useState<{ at: number | null; sprintId: string } | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const linkSprintId = params.get("replay");
    if (!linkSprintId) return null;
    const at = Number(params.get("t"));
    return { at: Number.isFinite(at) && at > 0 ? at : null, sprintId: linkSprintId };
  });
  const replayEligible = Boolean(
    isSprintBoard && !isBacklogTab && selectedBoard && selectedView && selectedView.type !== "timeline" && sprintScope && sprintScope !== "backlog",
  );
  const replayActive = Boolean(
    replayEligible && replaySession && replaySession.boardId === selectedBoard?.id && replaySession.sprintId === sprintScope,
  );
  const sprintReplay = useSprintReplay({
    board: selectedBoard,
    enabled: replayActive,
    sprintId: replayActive ? sprintScope : null,
    view: selectedView,
  });
  const replayFrame = replayActive ? sprintReplay.frame : null;
  const surfacedView =
    replayFrame && effectiveView ? { ...effectiveView, query: replayFrame.query } : effectiveView;
  const surfacedBoard =
    replayFrame && effectiveBoard ? { ...effectiveBoard, items: replayFrame.items } : effectiveBoard;
  // "Time in current status" badges on cards; short stays are noise, so only
  // runs of an hour or longer get a label.
  const replayStatusLabels = useMemo(() => {
    if (!replayFrame || sprintReplay.time === null) return null;
    const labels = new Map<string, string>();
    for (const [workObjectId, since] of replayFrame.statusSince) {
      const elapsed = sprintReplay.time - since;
      if (elapsed >= 60 * 60 * 1000) labels.set(workObjectId, formatReplayDuration(elapsed));
    }
    return labels;
  }, [replayFrame, sprintReplay.time]);

  // An external open-item request (inbox/search): once the target board is
  // selected, resolve the item — from the loaded page or the server — and
  // open its dialog.
  useEffect(() => {
    if (!openItemRequest || !selectedBoard || selectedBoard.id !== openItemRequest.boardId) return;
    let cancelled = false;
    void (async () => {
      const local = selectedBoard.items.find((candidate) => candidate.workObject.id === openItemRequest.workObjectId);
      const item =
        local ??
        (await scopeQueryClient
          .fetchQuery(
            scopeTrpc.boards.findItem.queryOptions({ boardId: openItemRequest.boardId, workObjectId: openItemRequest.workObjectId }),
          )
          .catch(() => null));
      if (!cancelled) {
        if (item) setSelectedBoardItem(item);
        onOpenItemRequestHandled?.();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onOpenItemRequestHandled, openItemRequest, scopeQueryClient, scopeTrpc, selectedBoard]);

  useEffect(() => {
    if (!pendingReplayLink || !isSprintBoard || !selectedBoard) return;
    if (!sprintTrack.data?.sprints.some((sprint) => sprint.id === pendingReplayLink.sprintId)) return;
    changeSprintScope(pendingReplayLink.sprintId);
    setReplaySession({ boardId: selectedBoard.id, sprintId: pendingReplayLink.sprintId });
  }, [changeSprintScope, isSprintBoard, pendingReplayLink, selectedBoard, sprintTrack.data?.sprints]);

  useEffect(() => {
    if (!pendingReplayLink || !replayActive || !sprintReplay.engine) return;
    if (pendingReplayLink.at !== null) sprintReplay.scrub(pendingReplayLink.at);
    setPendingReplayLink(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("replay");
    url.searchParams.delete("t");
    window.history.replaceState(null, "", url);
  }, [pendingReplayLink, replayActive, sprintReplay]);

  const refreshScopedQuery = useCallback(() => {
    void scopeQueryClient.invalidateQueries({ queryKey: scopeTrpc.boards.readViewQuery.queryKey() });
  }, [scopeQueryClient, scopeTrpc]);

  const handleLiveTreeInvalidated = useCallback(() => {
    void Promise.all([boards.reload(), folders.reload(), spaces.reload()]);
  }, [boards.reload, folders.reload, spaces.reload]);

  const handleLiveTreeReordered = useCallback((event: {
    payload: { items: Array<{ id: string; kind: "board" | "folder"; rank: string }>; parentFolderId: string | null };
  }) => {
    boards.applyTreeOrder(event.payload);
  }, [boards.applyTreeOrder]);

  useSpaceLiveEvents({
    onBoardItemGroupChanged: boards.applyBoardItemGroupPatch,
    onBoardViewOrderChanged: boards.applyBoardViewOrderPatch,
    onBoardUpdated: boards.applyBoardPatch,
    onFolderUpdated: folders.applyFolderPatch,
    onSpaceUpdated: spaces.applySpacePatch,
    onTreeInvalidated: handleLiveTreeInvalidated,
    onTreeReordered: handleLiveTreeReordered,
    spaceId: selectedBoard?.spaceId ?? "",
  });

  // When controlled, an unknown view id is resolved by the owner (the route
  // normalizer replaces it with the default view).
  const selectView = useCallback((viewId: string) => {
    setUncontrolledViewId(viewId);
    onSelectViewChange?.(viewId);
  }, [onSelectViewChange]);

  useEffect(() => {
    if (controlledSelectedViewId !== undefined) {
      return;
    }
    if (!selectedBoard || selectedBoard.views.some((view) => view.id === selectedViewId)) {
      return;
    }
    setUncontrolledViewId(selectedBoard.views.find((view) => view.isDefault)?.id ?? selectedBoard.views[0]?.id ?? "");
  }, [controlledSelectedViewId, selectedBoard, selectedViewId]);

  async function createWorkObjectOnBoard(
    input: Parameters<typeof workObjects.createWorkObject>[0],
    options?: {
      boardStatusId?: string;
      groupKey?: string;
      orderedWorkObjectIds?: string[];
      targetGroupValue?: string;
      viewId?: string;
    },
  ) {
    if (!selectedBoard) {
      return;
    }

    const created = await workObjects.createWorkObject(input);
    const nextData = await boards.addWorkObject({
      boardId: selectedBoard.id,
      boardStatusId: options?.boardStatusId,
      workObjectId: created.id,
    });
    const nextBoard = nextData?.boards.find((board) => board.id === selectedBoard.id);
    const boardItem = nextBoard?.items.find((item) => item.workObject.id === created.id);
    const orderedWorkObjectIds = [...(options?.orderedWorkObjectIds ?? []), created.id];
    if (boardItem && options?.groupKey && options.targetGroupValue !== undefined && options.viewId && !options.boardStatusId) {
      await boards.moveItemToViewGroup({
        boardId: selectedBoard.id,
        boardItemId: boardItem.id,
        boardViewId: options.viewId,
        groupKey: options.groupKey,
        nextWorkObjectId: null,
        orderedWorkObjectIds,
        previousWorkObjectId: orderedWorkObjectIds[orderedWorkObjectIds.length - 2] ?? null,
        targetGroupValue: options.targetGroupValue,
        workObjectId: created.id,
      });
    } else if (options?.groupKey && options.viewId) {
      await boards.replaceViewOrder({
        boardId: selectedBoard.id,
        boardViewId: options.viewId,
        groupKey: options.groupKey,
        workObjectIds: orderedWorkObjectIds,
      });
    }
    setIsCreateWorkOpen(false);
  }

  async function updateWorkObjectFieldValues(input: UpdateWorkObjectFieldValuesInput) {
    const rollback = boards.applyWorkObjectFieldValuesOptimistic({
      values: input.values,
      workObjectId: input.workObjectId,
    });
    try {
      const fieldValues = await workObjects.updateWorkObjectFieldValues(input);
      boards.applyWorkObjectFieldValuesPatch({
        fieldValues,
        workObjectId: input.workObjectId,
      });
    } catch (error) {
      rollback();
      throw error;
    }
  }

  // Убрать поле у одного типа карточек, не открывая экран полей. Правило то же,
  // что настраивается там: «поле применимо к типам …». Карточка лишь даёт до
  // него дотянуться в тот момент, когда стало видно, что поле здесь лишнее.
  async function hideFieldForType(field: WorkObjectFieldDefinition, typeId: string) {
    const everyTypeId = (workObjects.data?.types ?? []).map((type) => type.id);
    const current = field.appliesToAllTypes ? everyTypeId : field.applicableTypeIds;
    const nextTypeIds = current.filter((candidate) => candidate !== typeId);
    await fields.updateFieldConfiguration({
      applicableTypeIds: nextTypeIds,
      // Пустой список означал бы «применимо ко всем», поэтому поле, у которого
      // не осталось ни одного типа, уходит в архив: иначе оно тихо вернулось бы
      // на все карточки сразу.
      appliesToAllTypes: false,
      id: field.id,
      locations: field.locations
        .filter((location) => isConfigurableFieldLocationScope(location.scopeType))
        .map((location) => ({ isRequired: location.isRequired, scopeId: location.scopeId, scopeType: location.scopeType })),
    });
  }

  async function updateFormulaFieldConfiguration(field: WorkObjectFieldDefinition, expression: string) {
    // Mark the column as recomputing so its cells show a loader while the server
    // recomputes every item and the board reloads.
    setRecomputingFieldIds((current) => new Set(current).add(field.id));
    try {
      await fields.updateFieldConfiguration({
        applicableTypeIds: field.appliesToAllTypes ? [] : field.applicableTypeIds,
        appliesToAllTypes: field.appliesToAllTypes,
        config: {
          ...field.config,
          formula: { expression },
        },
        id: field.id,
        locations: field.locations
          .filter((location) => isConfigurableFieldLocationScope(location.scopeType))
          .map((location) => ({
            isRequired: location.isRequired,
            scopeId: location.scopeId,
            scopeType: location.scopeType,
          })),
        options: field.options.map((option) => ({
          color: option.color,
          id: option.id,
          rank: option.rank,
          value: option.value,
        })),
      });
      // The board grid reads item values from `boards.data`, so refetch the
      // boards in the background — reload keeps the current data until the new
      // list arrives, so the column updates in place without a remount/flash.
      await boards.reload();
    } finally {
      setRecomputingFieldIds((current) => {
        const next = new Set(current);
        next.delete(field.id);
        return next;
      });
    }
  }

  async function createView(type: BoardViewType, options?: { isPersonal?: boolean }) {
    if (!selectedBoard) {
      return;
    }
    const name = uniqueViewName(selectedBoard.views, t(boardViewMeta[type].labelKey));
    const createdViewId = await boards.createView({ boardId: selectedBoard.id, isPersonal: options?.isPersonal, name, type });
    if (createdViewId) {
      selectView(createdViewId);
    }
  }

  async function createDefaultView() {
    if (!selectedBoard) {
      return;
    }
    const createdViewId = await boards.createView({ boardId: selectedBoard.id, name: uniqueViewName(selectedBoard.views, t("board.list")), type: "list" });
    if (createdViewId) {
      selectView(createdViewId);
    }
  }

  async function duplicateView(source: BoardView["views"][number]) {
    if (!selectedBoard) {
      return;
    }
    // Копия делается одной серверной операцией: она переносит и настройки, и
    // признак личного вида. Имя приходит отсюда, потому что оно на языке
    // интерфейса, а уникальность доводит сервер.
    const createdViewId = await boards.duplicateView({
      boardId: selectedBoard.id,
      boardViewId: source.id,
      name: uniqueViewName(selectedBoard.views, t("board.viewCopyName", { name: source.name })),
    });
    if (createdViewId) {
      selectView(createdViewId);
    }
  }

  const isLoading = boards.isLoading || workObjects.isLoading || fields.isLoading || spaces.isLoading || folders.isLoading;
  const errors = [boards.error, spaces.error, folders.error, workObjects.error, fields.error].filter(Boolean);

  if (isLoading && !selectedBoard) {
    return <Skeleton className="h-72 w-full" />;
  }

  return (
    <RecomputingFieldsContext.Provider value={recomputingFieldIds}>
    <section className="flex h-full min-h-0 w-full min-w-0 flex-col gap-4">
      {errors.map((message) => (
        <Alert key={message} variant="destructive">
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      ))}

      {boards.data && !selectedBoard ? (
        <Empty className="min-h-[420px] justify-center">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <LayoutList />
            </EmptyMedia>
            <EmptyTitle>{t("board.noBoards")}</EmptyTitle>
            <EmptyDescription>{t("board.newBoardHint")}</EmptyDescription>
          </EmptyHeader>
          {showCreateBoardForm ? (
            <EmptyContent>
              <CreateBoardDialog
                disabled={boards.isMutating}
                folders={folders.data}
                onCreate={async (input) => {
                  const nextData = await boards.createBoard(input);
                  selectCreatedBoard(nextData, input, setSelectedBoardId);
                  setIsCreateBoardOpen(false);
                  await onBoardChanged?.();
                }}
                open={isCreateBoardOpen}
                spaces={spaces.data}
                onOpenChange={setIsCreateBoardOpen}
              />
            </EmptyContent>
          ) : null}
        </Empty>
      ) : null}

      {boards.data && selectedBoard ? (
        <div
          className={
            showBoardList
              ? "grid min-h-0 w-full min-w-0 flex-1 gap-4 xl:grid-cols-[240px_minmax(0,1fr)]"
              : "flex min-h-0 w-full min-w-0 flex-1"
          }
        >
          {showBoardList ? (
            <BoardList
              boards={boards.data.boards}
              selectedBoardId={selectedBoard.id}
              onCreateBoard={showCreateBoardForm ? () => setIsCreateBoardOpen(true) : undefined}
              onSelectBoard={setSelectedBoardId}
            />
          ) : null}

          <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-4">
            <BoardToolbar
              board={selectedBoard}
              currentView={isBacklogTab ? null : selectedView}
              disabled={boards.isMutating || workObjects.isCreating}
              fields={selectedBoardFields}
              isViewSettingsOpen={isViewSettingsOpen}
              sprintTabs={isSprintBoard ? { active: sprintTab, onSelect: setSprintTab } : undefined}
              navigationSlot={navigationSlot}
              onChangeFilter={boards.updateViewFilter}
              onChangeGroup={boards.updateViewGroup}
              onChangeSort={boards.updateViewSort}
              onCreateView={createView}
              onCreateWork={() => setIsCreateWorkOpen(true)}
              onArchiveBoard={async () => {
                await boards.archiveBoard({ boardId: selectedBoard.id });
                await onBoardChanged?.();
              }}
              onOpenBoardSettings={() => setIsBoardSettingsOpen(true)}
              onOpenPermissions={() => setIsBoardPermissionsOpen(true)}
              onOpenStatuses={() => setIsStatusManagerOpen(true)}
              onOpenImport={() => setIsImportDialogOpen(true)}
              onOpenTemplates={() => setIsTemplateDialogOpen(true)}
              onToggleViewSettings={() => setIsViewSettingsOpen((open) => !open)}
              onSelectView={(viewId) => {
                setSprintTab(null);
                selectView(viewId);
              }}
              onArchiveView={async (boardViewId) => {
                await boards.archiveView({ boardId: selectedBoard.id, boardViewId });
              }}
              onRenameView={boards.renameView}
              onSetDefaultView={async (boardViewId) => {
                await boards.setDefaultView({ boardId: selectedBoard.id, boardViewId });
              }}
              trailingControls={
                isSprintBoard && !isBacklogTab ? (
                  <>
                    {replayEligible && selectedBoard && sprintScope ? (
                      <Button
                        className={cn(!replayActive && "text-muted-foreground")}
                        onClick={() =>
                          setReplaySession(replayActive ? null : { boardId: selectedBoard.id, sprintId: sprintScope })
                        }
                        size="sm"
                        variant={replayActive ? "secondary" : "ghost"}
                      >
                        <History data-icon="inline-start" />
                        {t("board.replay")}
                      </Button>
                    ) : null}
                    <SprintScopeSwitcher onChange={changeSprintScope} sprints={sprintTrack.data?.sprints ?? []} value={sprintScope} />
                  </>
                ) : null
              }
            />

            {replayActive ? (
              <SprintReplayBar
                canUseDayRange={sprintReplay.canUseDayRange}
                caption={sprintReplay.caption}
                hasNextKey={sprintReplay.hasNextKey}
                hasPreviousKey={sprintReplay.hasPreviousKey}
                isLoading={sprintReplay.isLoading}
                onChangeRange={sprintReplay.changeRange}
                onCopyLink={() => {
                  if (!sprintScope || sprintReplay.time === null) return;
                  const url = new URL(window.location.href);
                  url.searchParams.set("replay", sprintScope);
                  url.searchParams.set("t", String(Math.round(sprintReplay.time)));
                  void navigator.clipboard.writeText(url.toString());
                }}
                onExit={() => setReplaySession(null)}
                onJumpToNextKey={sprintReplay.jumpToNextKey}
                onJumpToPreviousKey={sprintReplay.jumpToPreviousKey}
                onScrub={sprintReplay.scrub}
                onTogglePlay={sprintReplay.togglePlay}
                playing={sprintReplay.playing}
                range={sprintReplay.range}
                steps={sprintReplay.visibleSteps}
                time={sprintReplay.time}
                windowEnd={sprintReplay.windowEnd}
                windowStart={sprintReplay.windowStart}
              />
            ) : null}

            {/* overflow-clip, not hidden: `hidden` leaves the element
                programmatically scrollable (and computes overflow-y to auto),
                so dnd-kit's auto-scroller drags this wrapper around before the
                board's own scroller. `clip` forbids scrolling entirely. */}
            <div className="relative flex min-h-0 min-w-0 flex-1 overflow-x-clip">
              <div className={cn("relative min-h-0 min-w-0 flex-1 overflow-x-clip", replayActive && "lg:mr-[18.75rem]")}>
              {sprintTab === "report" && isSprintBoard ? (
                <SprintReportSurface
                  board={selectedBoard}
                  fields={selectedBoardFields}
                  onOpenItem={(workObjectId) => {
                    const item = selectedBoard.items.find((candidate) => candidate.workObject.id === workObjectId);
                    if (item) setSelectedBoardItem(item);
                  }}
                  onOpenReplay={(sprintId) => {
                    changeSprintScope(sprintId);
                    setSprintTab(null);
                    setReplaySession({ boardId: selectedBoard.id, sprintId });
                  }}
                />
              ) : sprintTab === "backlog" && isSprintBoard ? (
                <SprintPlanningSurface
                  board={selectedBoard}
                  onOpenItem={(workObjectId) => {
                    const item = selectedBoard.items.find((candidate) => candidate.workObject.id === workObjectId);
                    if (item) setSelectedBoardItem(item);
                  }}
                  spaceBoards={(boards.data?.boards ?? []).filter((board) => board.spaceId === selectedBoard.spaceId)}
                />
              ) : (
                <SprintReplayStatusSinceContext.Provider value={replayStatusLabels}>
                <BoardViewSurface
                  board={surfacedBoard ?? selectedBoard}
                  disabled={boards.isMutating || replayActive}
                  fields={selectedBoardFields}
                  sprintScope={isSprintBoard && !isBacklogTab ? sprintScope : null}
                  sprintTrackBoardId={sprintTrackBoardId}
                  onArchive={async (input) => {
                    await boards.archiveItem(input);
                    refreshScopedQuery();
                  }}
                  onCreateDefaultView={createDefaultView}
                  onCreateWork={() => setIsCreateWorkOpen(true)}
                  onCreateWorkInline={async (input, options) => {
                    await createWorkObjectOnBoard(input, options);
                    refreshScopedQuery();
                  }}
                  onMoveOrder={async (input) => {
                    await boards.moveViewOrder(input);
                  }}
                  onMoveToGroup={async (input) => {
                    await boards.moveItemToViewGroup(input);
                    refreshScopedQuery();
                  }}
                  loadingViewPageKey={boards.loadingViewPageKey}
                  onLoadNextViewPage={boards.loadNextViewPage}
                  onOpenItem={setSelectedBoardItem}
                  onReplaceOrder={async (input) => {
                    await boards.replaceViewOrder(input);
                  }}
                  onChangeSort={boards.updateViewSort}
                  onChangeVisibleFields={boards.updateViewFields}
                  onUpdate={async (input) => {
                    const result = await boards.updateItem(input);
                    refreshScopedQuery();
                    return result;
                  }}
                  onUpdateFieldValues={updateWorkObjectFieldValues}
                  onUpdateFormulaField={updateFormulaFieldConfiguration}
                  onUpdateType={boards.updateWorkObjectType}
                  types={workObjects.data?.types ?? []}
                  users={workObjects.data?.users ?? []}
                  view={surfacedView}
                />
                </SprintReplayStatusSinceContext.Provider>
              )}

              {!isBacklogTab && !replayActive && effectiveView?.query.nextCursor ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center">
                  <Button
                    className="pointer-events-auto shadow-lg"
                    disabled={boards.loadingViewPageKey === `${effectiveView.id}:__all__`}
                    onClick={() => void boards.loadNextViewPage({ boardId: selectedBoard.id, boardViewId: effectiveView.id })}
                    size="sm"
                    variant="secondary"
                  >
                    {boards.loadingViewPageKey === `${effectiveView.id}:__all__` ? <Loader2 className="animate-spin" /> : null}
                    Load more
                  </Button>
                </div>
              ) : null}
              </div>

              {replayActive ? (
                <SprintReplayLog activeAt={sprintReplay.activeStepAt} onJump={sprintReplay.scrub} steps={sprintReplay.visibleSteps} />
              ) : null}

              {selectedView ? (
                <ViewSettingsSheet
                  board={selectedBoard}
                  disabled={boards.isMutating}
                  fields={selectedBoardFields}
                  open={isViewSettingsOpen}
                  view={selectedView}
                  onArchive={async (boardViewId) => {
                    await boards.archiveView({ boardId: selectedBoard.id, boardViewId });
                    setIsViewSettingsOpen(false);
                  }}
                  onOpenChange={setIsViewSettingsOpen}
                  onSetDefault={async (boardViewId) => {
                    await boards.setDefaultView({ boardId: selectedBoard.id, boardViewId });
                  }}
                  onChangeGroup={boards.updateViewGroup}
                  onChangeFilter={boards.updateViewFilter}
                  onChangeLayout={boards.updateViewLayout}
                  onChangeVisibility={boards.updateViewVisibility}
                  onChangeSort={boards.updateViewSort}
                  onChangeType={boards.changeViewType}
                  onChangeVisibleFields={boards.updateViewFields}
                  onDuplicate={async (boardViewId) => {
                    const source = selectedBoard.views.find((item) => item.id === boardViewId);
                    if (source) await duplicateView(source);
                  }}
                  onRename={boards.renameView}
                />
              ) : null}
            </div>
          </div>

          {showCreateBoardForm ? (
            <CreateBoardDialog
              disabled={boards.isMutating}
              folders={folders.data}
              onCreate={async (input) => {
                const nextData = await boards.createBoard(input);
                selectCreatedBoard(nextData, input, setSelectedBoardId);
                setIsCreateBoardOpen(false);
                await onBoardChanged?.();
              }}
              open={isCreateBoardOpen}
              spaces={spaces.data}
              onOpenChange={setIsCreateBoardOpen}
            />
          ) : null}

          {workObjects.data ? (
            <CreateWorkObjectDialog
              defaultTypeId={mostFrequentTypeId(selectedBoard.items)}
              fieldDefinitions={selectedBoardFields}
              isCreating={workObjects.isCreating || boards.isMutating}
              listView={workObjects.data}
              open={isCreateWorkOpen}
              onCreate={createWorkObjectOnBoard}
              onOpenChange={setIsCreateWorkOpen}
              rememberKey={selectedBoard.id}
            />
          ) : null}

          <BoardSettingsDialog
            board={selectedBoard}
            disabled={boards.isMutating}
            open={isBoardSettingsOpen}
            onArchive={async () => {
              await boards.archiveBoard({ boardId: selectedBoard.id });
              setIsBoardSettingsOpen(false);
              await onBoardChanged?.();
            }}
            onOpenChange={setIsBoardSettingsOpen}
          />
          <ResourceAccessDialog
            name={selectedBoard.name}
            onOpenChange={setIsBoardPermissionsOpen}
            open={isBoardPermissionsOpen}
            scope={{ id: selectedBoard.id, type: "board" }}
          />

          <BoardStatusManagerDialog
            board={selectedBoard}
            disabled={boards.isMutating}
            onArchiveStatus={boards.archiveStatus}
            onClearStatusEntryRule={boards.clearStatusEntryRule}
            onCreateStatus={boards.createStatus}
            onMoveStatus={boards.moveStatus}
            onOpenChange={setIsStatusManagerOpen}
            onSetDefaultStatus={boards.setDefaultStatus}
            onUpdateStatus={boards.updateStatus}
            onUpdateStatusEntryRule={boards.updateStatusEntryRule}
            open={isStatusManagerOpen}
          />

          <ImportItemsDialog
            boardId={selectedBoard.id}
            fields={fields.data}
            onImported={async () => {
              await boards.reload();
              refreshScopedQuery();
            }}
            onOpenChange={setIsImportDialogOpen}
            open={isImportDialogOpen}
          />

          <ApplyBoardTemplateDialog
            boardId={selectedBoard.id}
            onApplied={async () => {
              await boards.reload();
              await fields.reload();
            }}
            onOpenChange={setIsTemplateDialogOpen}
            open={isTemplateDialogOpen}
          />

          {workObjects.data ? (
            <WorkObjectDetailDialog
              auditEvents={selectedItemAudit.data}
              fieldDefinitions={selectedBoardFields}
              fieldValues={selectedItemFieldValues.data}
              isArchiving={workObjects.isArchiving}
              isLoadingAudit={selectedItemAudit.isLoading}
              isLoadingFieldValues={selectedItemFieldValues.isLoading}
              isSaving={workObjects.isUpdating}
              item={selectedBoardItem?.workObject ?? null}
              listView={workObjects.data}
              onHideFieldForType={hideFieldForType}
              onOpenRelated={(workObjectId) => {
                // Связанная карточка часто живёт на ДРУГОЙ доске: история — на
                // продуктовой, задача — на инженерной. Раньше в этом случае
                // клик по связи молча ничего не делал, и переход по ветке
                // работал только внутри одной доски.
                const here = selectedBoard.items.find((candidate) => candidate.workObject.id === workObjectId);
                if (here) {
                  setSelectedBoardItem(here);
                  return;
                }
                const elsewhere = boards.data?.boards
                  .flatMap((board) => board.items.map((candidate) => ({ board, candidate })))
                  .find((entry) => entry.candidate.workObject.id === workObjectId);
                if (!elsewhere) return;
                setSelectedBoardId(elsewhere.board.id);
                setSelectedBoardItem(elsewhere.candidate);
              }}
              referenceLabels={selectedBoardItem?.relationLabels}
              statusControl={
                selectedBoardItem ? (
                  <BoardStatusSelect
                    board={selectedBoard}
                    disabled={boards.isMutating}
                    item={selectedBoardItem}
                    onUpdate={async (input) => {
                      await boards.updateItem(input);
                      refreshScopedQuery();
                      // A status move is part of the item's history now, so the
                      // activity feed has to pick it up straight away.
                      await selectedItemAudit.reload();
                      const nextStatus = selectedBoard.statuses.find((status) => status.id === input.boardStatusId);
                      setSelectedBoardItem((current) => (current && nextStatus ? { ...current, status: nextStatus } : current));
                    }}
                  />
                ) : null
              }
              onArchive={async (id) => {
                await workObjects.archiveWorkObject(id);
                setSelectedBoardItem(null);
                await boards.reload();
              }}
              onOpenChange={(nextOpen) => {
                if (!nextOpen) setSelectedBoardItem(null);
              }}
              onSave={async (input, values) => {
                const updated = await workObjects.updateWorkObjectDetails({ ...input, values });
                selectedItemFieldValues.replaceLocalValues(updated.fieldValues);
                await Promise.all([selectedItemFieldValues.reload(), selectedItemAudit.reload(), boards.reload()]);
                const refreshedItem = boards.data?.boards.flatMap((board) => board.items).find((item) => item.workObject.id === updated.workObject.id);
                setSelectedBoardItem((current) => current ? { ...current, workObject: { ...current.workObject, ...updated.workObject } } : refreshedItem ?? null);
                return updated.workObject;
              }}
              open={selectedBoardItem !== null}
            />
          ) : null}

        </div>
      ) : null}
    </section>
    </RecomputingFieldsContext.Provider>
  );
}

function BoardList({
  boards,
  selectedBoardId,
  onCreateBoard,
  onSelectBoard,
}: {
  boards: BoardView[];
  selectedBoardId: string;
  onCreateBoard?: () => void;
  onSelectBoard: (boardId: string) => void;
}) {
  const t = useT();
  const orderedBoards = orderBoardsForNavigation(boards);
  return (
    <aside className="flex min-w-0 flex-col gap-2">
      <div className="flex h-8 items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">{t("board.boards")}</p>
        {onCreateBoard ? (
          <Button aria-label={t("board.createBoard")} onClick={onCreateBoard} size="icon-sm" variant="ghost">
            <Plus />
          </Button>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        {orderedBoards.map((board) => (
          <button
            className={cn(
              "flex h-9 min-w-0 items-center justify-between gap-2 rounded-md px-2 text-left text-xs transition-colors",
              board.id === selectedBoardId ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
            key={board.id}
            onClick={() => onSelectBoard(board.id)}
            type="button"
          >
            <span className="min-w-0 truncate font-medium">{board.name}</span>
            <span className="shrink-0 tabular-nums">{board.items.length}</span>
          </button>
        ))}
      </div>
    </aside>
  );
}

const sprintTabValues = { backlog: "__backlog__", report: "__report__" } as const;

function BoardToolbar({
  board,
  currentView,
  disabled,
  fields,
  isViewSettingsOpen,
  navigationSlot,
  onChangeFilter,
  onChangeGroup,
  onChangeSort,
  onCreateView,
  onCreateWork,
  onArchiveBoard,
  onOpenBoardSettings,
  onOpenPermissions,
  onOpenStatuses,
  onOpenTemplates,
  onOpenImport,
  onToggleViewSettings,
  onSelectView,
  onArchiveView,
  onRenameView,
  onSetDefaultView,
  sprintTabs,
  trailingControls,
}: {
  sprintTabs?: { active: "backlog" | "report" | null; onSelect: (tab: "backlog" | "report") => void };
  board: BoardView;
  currentView: BoardView["views"][number] | null;
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  isViewSettingsOpen: boolean;
  navigationSlot?: ReactNode;
  onArchiveBoard: () => Promise<void>;
  onArchiveView: (boardViewId: string) => Promise<void>;
  onChangeFilter: (input: { boardId: string; boardViewId: string; filterExpression: string | null }) => Promise<void>;
  onChangeGroup: (input: { boardId: string; boardViewId: string; groupBucket?: BoardViewConfig["groupBucket"]; groupBy: BoardViewConfig["groupBy"] }) => Promise<void>;
  onChangeSort: (input: { boardId: string; boardViewId: string; sorts: BoardViewConfig["sorts"] }) => Promise<void>;
  onCreateView: (type: BoardViewType, options?: { isPersonal?: boolean }) => Promise<void>;
  onCreateWork: () => void;
  onOpenBoardSettings: () => void;
  onOpenPermissions: () => void;
  onOpenStatuses: () => void;
  onOpenTemplates: () => void;
  onOpenImport: () => void;
  onToggleViewSettings: () => void;
  onSelectView: (boardViewId: string) => void;
  onRenameView: (input: { boardId: string; boardViewId: string; name: string }) => Promise<void>;
  onSetDefaultView: (boardViewId: string) => Promise<void>;
  trailingControls?: ReactNode;
}) {
  const t = useT();
  const viewConfig = currentView ? normalizeViewConfig(currentView.config) : null;
  const viewFields = currentView ? normalizeViewFields(currentView) : [];
  const showQuickControls = Boolean(currentView) && currentView?.type !== "form";
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; name: string } | null>(null);
  return (
    <header className="flex w-full min-w-0 flex-col gap-3 border-b border-border/70 pb-3">
      <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          {navigationSlot}
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate text-base font-semibold">{board.name}</h1>
              {board.purpose === "default" ? <Badge variant="secondary">{t("board.defaultBadge")}</Badge> : null}
            </div>
            {board.description ? <p className="mt-1 max-w-2xl truncate text-xs text-muted-foreground">{board.description}</p> : null}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button disabled={disabled} onClick={onCreateWork} size="sm">
            <Plus data-icon="inline-start" />
            {t("board.workItem")}
          </Button>
          <Button disabled={disabled} onClick={onOpenPermissions} size="sm" variant="outline">
            <Share2 data-icon="inline-start" />
            {t("board.accessShare")}
          </Button>
          <BoardActionsMenu
            archiveTitle={`Archive "${board.name}"?`}
            ariaLabel={t("a11y.boardOptions", { name: board.name })}
            disabled={disabled}
            dropdownTrigger={
              <Button aria-label={t("a11y.boardOptions", { name: board.name })} size="icon-sm" variant="outline">
                <MoreHorizontal />
              </Button>
            }
            onArchive={onArchiveBoard}
            onOpenSettings={onOpenBoardSettings}
            onOpenPermissions={onOpenPermissions}
            onOpenImport={onOpenImport}
            onOpenStatuses={onOpenStatuses}
            onOpenTemplates={onOpenTemplates}
          />
        </div>
      </div>

      <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
        {board.views.length > 0 ? (
          <Tabs
            className="min-w-0"
            value={sprintTabs?.active ? sprintTabValues[sprintTabs.active] : currentView?.id ?? ""}
            onValueChange={(value) => {
              if (typeof value !== "string") {
                return;
              }
              if (value === sprintTabValues.backlog) {
                sprintTabs?.onSelect("backlog");
                return;
              }
              if (value === sprintTabValues.report) {
                sprintTabs?.onSelect("report");
                return;
              }
              onSelectView(value);
            }}
          >
            <TabsList className="no-scrollbar max-w-full overflow-x-auto overflow-y-hidden pb-1" variant="line">
              {sprintTabs ? (
                <>
                  <TabsTrigger className="after:bottom-0" value={sprintTabValues.backlog}>
                    <LayoutList />
                    {t("sprint.backlog")}
                  </TabsTrigger>
                  <TabsTrigger className="after:bottom-0" value={sprintTabValues.report}>
                    <ChartColumn />
                    {t("board.report")}
                  </TabsTrigger>
                </>
              ) : null}
              {orderViewsForNavigation(board.views).map((view) => (
                <ContextMenu key={view.id}>
                  <ContextMenuTrigger render={<TabsTrigger className="after:bottom-0" value={view.id} />}>
                    {view.ownerUserId ? <Lock aria-label={t("board.personalViewBadge")} /> : boardViewMeta[view.type].icon}
                    {currentView?.id === view.id ? (
                      <EditableText
                        ariaLabel={t("board.viewName")}
                        className="max-w-32"
                        editTrigger="doubleClick"
                        inputClassName="text-xs font-medium leading-none"
                        onChange={(nextName) => {
                          const name = nextName.trim();
                          if (name.length < 2 || name === view.name) {
                            return;
                          }
                          void onRenameView({ boardId: board.id, boardViewId: view.id, name });
                        }}
                        placeholder={t("board.view")}
                        previewClassName="text-xs font-medium leading-none"
                        value={view.name}
                      />
                    ) : (
                      <span className="max-w-32 truncate">{view.name}</span>
                    )}
                  </ContextMenuTrigger>
                  <ContextMenuContent className="w-52">
                    {view.ownerUserId ? (
                      <ContextMenuGroup>
                        <ContextMenuLabel className="whitespace-normal font-normal text-muted-foreground">
                          {t("board.personalViewBadge")}
                        </ContextMenuLabel>
                      </ContextMenuGroup>
                    ) : (
                      <ContextMenuItem
                        disabled={disabled || view.isDefault}
                        onClick={() => {
                          void onSetDefaultView(view.id);
                        }}
                      >
                        <Star />
                        {view.isDefault ? t("board.defaultView") : t("board.setDefaultView")}
                      </ContextMenuItem>
                    )}
                    <ContextMenuItem
                      disabled={disabled || (!view.ownerUserId && board.views.filter((item) => !item.ownerUserId).length <= 1)}
                      variant="destructive"
                      onClick={() => setArchiveTarget({ id: view.id, name: view.name })}
                    >
                      <Trash2 />
                      {t("board.archiveView")}
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ))}
            </TabsList>
          </Tabs>
        ) : (
          <p className="text-xs text-muted-foreground">{t("board.noActiveViews")}</p>
        )}
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button className="shrink-0 self-center text-muted-foreground hover:text-foreground" size="sm" variant="ghost">
                  <Plus data-icon="inline-start" />
                  {t("board.view")}
                </Button>
              }
            />
            <DropdownMenuContent align="start" className="w-44">
              <DropdownMenuGroup>
                {boardViewTypes.map((type) => (
                  <DropdownMenuItem
                    key={type}
                    onClick={() => {
                      void onCreateView(type);
                    }}
                  >
                    {boardViewMeta[type].icon}
                    {t(boardViewMeta[type].labelKey)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Lock />
                  {t("board.personalView")}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-56">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="whitespace-normal font-normal text-muted-foreground">
                      {t("board.personalViewHint")}
                    </DropdownMenuLabel>
                    {boardViewTypes.map((type) => (
                      <DropdownMenuItem
                        key={type}
                        onClick={() => {
                          void onCreateView(type, { isPersonal: true });
                        }}
                      >
                        {boardViewMeta[type].icon}
                        {t(boardViewMeta[type].labelKey)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {trailingControls}
          {showQuickControls && currentView && viewConfig ? (
            <>
              <FilterEditorPopover
                align="end"
                config={viewConfig}
                disabled={disabled}
                fields={fields}
                onCommit={(filterExpression) => onChangeFilter({ boardId: board.id, boardViewId: currentView.id, filterExpression })}
                trigger={
                  <ViewControlChip active={Boolean(viewConfig.filterExpression)} icon={<Filter />} label={viewConfig.filterExpression ? t("board.filtered") : t("board.filter")} />
                }
              />
              <Popover>
                <PopoverTrigger render={<ViewControlChip active={Boolean(viewConfig.groupBy)} icon={<Columns3 />} label={viewConfig.groupBy ? t("board.groupWith", { field: groupByLabel(viewConfig.groupBy, fields) }) : t("board.group")} />} />
                <PopoverContent align="end" className="w-64 gap-2 p-2.5">
                  <p className="px-0.5 text-xs font-medium text-muted-foreground">{t("board.groupBy")}</p>
                  <ViewGroupControl
                    config={viewConfig}
                    disabled={disabled}
                    fields={fields}
                    groupValues={currentView.query.groups.map((group) => group.value)}
                    onCommit={(groupBy, groupBucket) =>
                      void onChangeGroup({ boardId: board.id, boardViewId: currentView.id, groupBucket, groupBy })
                    }
                  />
                </PopoverContent>
              </Popover>
              {/* A timeline has no row order to sort — position comes from
                  dates and lane packing. */}
              {currentView.type !== "timeline" ? (
                <Popover>
                  <PopoverTrigger render={<ViewControlChip active={viewConfig.sorts.length > 0} icon={<ArrowUpDown />} label={viewConfig.sorts.length > 0 ? t("board.sortWithCount", { count: viewConfig.sorts.length }) : t("board.sort")} />} />
                  <PopoverContent align="end" className="w-[24rem] max-w-[calc(100vw-2rem)] gap-2 p-3">
                    <ViewSortControl
                      config={viewConfig}
                      disabled={disabled}
                      fields={fields}
                      onCommit={(sorts) => void onChangeSort({ boardId: board.id, boardViewId: currentView.id, sorts })}
                      viewFields={viewFields}
                    />
                  </PopoverContent>
                </Popover>
              ) : null}
              <span className="mx-0.5 h-5 w-px bg-border/70" />
            </>
          ) : null}
          <Button
            aria-pressed={isViewSettingsOpen}
            className={cn(isViewSettingsOpen && "border-primary/40 bg-primary/15 text-primary hover:bg-primary/20 hover:text-primary")}
            disabled={!currentView}
            onClick={onToggleViewSettings}
            size="sm"
            variant="outline"
          >
            <Settings2 data-icon="inline-start" />
            {t("board.viewSettings")}
          </Button>
        </div>
      </div>
      <ArchiveViewDialog
        disabled={disabled}
        onConfirm={async () => {
          if (archiveTarget) await onArchiveView(archiveTarget.id);
        }}
        onOpenChange={(next) => {
          if (!next) setArchiveTarget(null);
        }}
        open={Boolean(archiveTarget)}
        viewName={archiveTarget?.name ?? ""}
      />
    </header>
  );
}

function ArchiveViewDialog({
  disabled,
  onConfirm,
  onOpenChange,
  open,
  viewName,
}: {
  disabled: boolean;
  onConfirm: () => Promise<void>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  viewName: string;
}) {
  const t = useT();
  const [isArchiving, setIsArchiving] = useState(false);
  async function confirm() {
    setIsArchiving(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setIsArchiving(false);
    }
  }
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive “{viewName}”?</AlertDialogTitle>
          <AlertDialogDescription>This view and its layout, filters, and sorting will be archived. Work items stay on the board.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isArchiving}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            disabled={disabled || isArchiving}
            onClick={() => {
              void confirm();
            }}
            variant="destructive"
          >
            {isArchiving ? "Archiving…" : t("board.archiveView")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CreateBoardDialog({
  disabled,
  folders,
  onCreate,
  onOpenChange,
  open,
  spaces,
}: {
  disabled: boolean;
  folders: FolderDefinition[];
  onCreate: (input: {
    description: string;
    folderId?: string | null;
    name: string;
    purpose: BoardPurpose;
    spaceId: string;
    templateKey?: string;
  }) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  spaces: Array<{ id: string; name: string }>;
}) {
  const t = useT();
  const trpc = useTRPC();
  const [description, setDescription] = useState("");
  const [folderId, setFolderId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [spaceId, setSpaceId] = useState("");
  // A starting configuration is optional: "Empty" keeps the board bare.
  const [templateKey, setTemplateKey] = useState("empty");
  const { locale: templateLocale } = useI18n();
  const templates = useQuery(trpc.boards.templates.queryOptions({ locale: templateLocale }));
  const availableFolders = folders.filter((folder) => folder.spaceId === spaceId);

  useEffect(() => {
    setSpaceId((current) => current || (spaces[0]?.id ?? ""));
  }, [spaces]);

  useEffect(() => {
    if (folderId && !availableFolders.some((folder) => folder.id === folderId)) {
      setFolderId(null);
    }
  }, [availableFolders, folderId]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!spaceId || name.trim().length < 1) {
      return;
    }
    await onCreate({
      description,
      folderId,
      name,
      purpose: "custom",
      spaceId,
      templateKey: templateKey === "empty" ? undefined : templateKey,
    });
    setDescription("");
    setFolderId(null);
    setName("");
    setTemplateKey("empty");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("board.createBoard")}</DialogTitle>
          <DialogDescription>{t("board.chooseSpace")}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <label className="flex flex-col gap-1.5 text-xs font-medium">
            {t("board.name")}
            <Input autoFocus onChange={(event) => setName(event.target.value)} placeholder={t("board.roadmap")} value={name} />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-xs font-medium">
              {t("board.space")}
              <Select
                value={spaceId}
                onValueChange={(nextValue) => {
                  if (nextValue !== null) {
                    setSpaceId(nextValue);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("board.selectSpace")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {spaces.map((space) => (
                      <SelectItem key={space.id} value={space.id}>
                        {space.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </label>
            <label className="flex flex-col gap-1.5 text-xs font-medium">
              {t("board.parent")}
              <Select
                value={folderId ?? "space-root"}
                onValueChange={(nextValue) => {
                  setFolderId(nextValue && nextValue !== "space-root" ? nextValue : null);
                }}
              >
                <SelectTrigger>
                  <SelectValue>
                    {folderId
                      ? availableFolders.find((folder) => folder.id === folderId)?.name ?? t("board.spaceRoot")
                      : t("board.spaceRoot")}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="space-root">{t("board.spaceRoot")}</SelectItem>
                    {availableFolders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </label>
          </div>
          <label className="flex flex-col gap-1.5 text-xs font-medium">
            {t("board.startFrom")}
            <Select value={templateKey} onValueChange={(nextValue) => setTemplateKey(nextValue ?? "empty")}>
              <SelectTrigger>
                <SelectValue>
                  {templateKey === "empty"
                    ? t("board.emptyBoard")
                    : (templates.data ?? []).find((template) => template.key === templateKey)?.name ?? t("board.emptyBoard")}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="empty">{t("board.emptyBoard")}</SelectItem>
                  {(templates.data ?? []).map((template) => (
                    <SelectItem key={template.key} value={template.key}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <span className="font-normal text-muted-foreground">
              {templates.data?.find((template) => template.key === templateKey)?.description ??
                "Fields, statuses and views are added by hand."}
            </span>
          </label>
          <label className="flex flex-col gap-1.5 text-xs font-medium">
            {t("board.description")}
            <Textarea onChange={(event) => setDescription(event.target.value)} placeholder={t("board.optionalContext")} value={description} />
          </label>
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>{t("common.cancel")}</DialogClose>
            <Button disabled={disabled || !spaceId || name.trim().length < 1} type="submit">
              {t("board.createBoard")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Какой тип предложить в форме создания: тот, которого на доске больше всего.
// На доске багов это баг, на доске планирования — задача; раньше всегда был
// первый в списке, то есть Epic.
function mostFrequentTypeId(items: BoardItemView[]): string | null {
  const counts = new Map<string, number>();
  for (const item of items) {
    const typeId = item.workObject.type?.id;
    if (typeId) counts.set(typeId, (counts.get(typeId) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [typeId, count] of counts) {
    if (count > bestCount) {
      best = typeId;
      bestCount = count;
    }
  }
  return best;
}

function CreateWorkObjectDialog({
  defaultTypeId,
  fieldDefinitions,
  isCreating,
  listView,
  onCreate,
  onOpenChange,
  open,
  rememberKey,
}: {
  defaultTypeId?: string | null;
  fieldDefinitions: WorkObjectFieldDefinition[];
  isCreating: boolean;
  listView: Parameters<typeof CreateWorkObjectForm>[0]["listView"];
  onCreate: Parameters<typeof CreateWorkObjectForm>[0]["onCreate"];
  onOpenChange: (open: boolean) => void;
  open: boolean;
  rememberKey?: string | null;
}) {
  const t = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogTitle className="sr-only">{t("board.createItem")}</DialogTitle>
        <CreateWorkObjectForm
          defaultTypeId={defaultTypeId}
          fieldDefinitions={fieldDefinitions}
          isCreating={isCreating}
          listView={listView}
          onCreate={onCreate}
          rememberKey={rememberKey}
        />
      </DialogContent>
    </Dialog>
  );
}

function ViewSettingsSheet({
  board,
  disabled,
  fields,
  onArchive,
  onChangeGroup,
  onChangeFilter,
  onChangeLayout,
  onChangeVisibility,
  onChangeSort,
  onChangeType,
  onChangeVisibleFields,
  onDuplicate,
  onOpenChange,
  onRename,
  onSetDefault,
  open,
  view,
}: {
  board: BoardView;
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  onArchive: (boardViewId: string) => Promise<void>;
  onChangeGroup: (input: { boardId: string; boardViewId: string; groupBucket?: BoardViewConfig["groupBucket"]; groupBy: BoardViewConfig["groupBy"] }) => Promise<void>;
  onChangeFilter: (input: { boardId: string; boardViewId: string; filterExpression: string | null }) => Promise<void>;
  onChangeLayout: (input: { boardId: string; boardViewId: string; layout: Partial<BoardViewLayout> }) => Promise<void>;
  onChangeVisibility: (input: { boardId: string; boardViewId: string; includeClosedItems: boolean }) => Promise<void>;
  onChangeSort: (input: { boardId: string; boardViewId: string; sorts: BoardViewConfig["sorts"] }) => Promise<void>;
  onChangeType: (input: { boardId: string; boardViewId: string; type: BoardViewType }) => Promise<void>;
  onChangeVisibleFields: (input: { boardId: string; boardViewId: string; fields: Array<{ fieldDefinitionId: string; summaryOperator?: BoardViewField["summaryOperator"]; visible: boolean }> }) => Promise<void>;
  onDuplicate: (boardViewId: string) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  onRename: (input: { boardId: string; boardViewId: string; name: string }) => Promise<void>;
  onSetDefault: (boardViewId: string) => Promise<void>;
  open: boolean;
  view: BoardView["views"][number];
}) {
  const t = useT();
  const [config, setConfig] = useState<BoardViewConfig>(normalizeViewConfig(view.config));
  const [viewFields, setViewFields] = useState<BoardViewField[]>(normalizeViewFields(view));
  const [name, setName] = useState(view.name);
  const [type, setType] = useState<BoardViewType>(view.type);
  const [activeSection, setActiveSection] = useState<"fields" | "filter" | "group" | "layout" | "root" | "sort">("root");
  const isPersonalView = Boolean(view.ownerUserId);
  // A personal view is nobody else's business, so removing it never leaves the
  // board without a way in; a shared board still needs one view left standing.
  const canArchive = isPersonalView || board.views.filter((item) => !item.ownerUserId).length > 1;
  const lockedFieldIds = fields.filter((field) => field.key === "title").map((field) => field.id);
  const supportsGrouping = type !== "form" && type !== "dashboard";
  // A timeline has no row order to sort — position comes from dates and lanes.
  const supportsSorting = supportsGrouping && type !== "timeline";
  const visibleFieldCount = viewFields.filter((field) => field.visible).length;
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<"csv" | "json" | "xlsx" | null>(null);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const linkCopiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (linkCopiedTimeoutRef.current) {
      clearTimeout(linkCopiedTimeoutRef.current);
    }
  }, []);

  // The export is fetched, then handed to the browser as a file: no extra
  // endpoint and no capability URL for something the session already covers.
  async function exportView(format: "csv" | "json" | "xlsx") {
    setExportingFormat(format);
    try {
      const file = await queryClient.fetchQuery(
        trpc.boards.exportView.queryOptions({ boardId: view.boardId, boardViewId: view.id, format }),
      );
      const bytes =
        file.encoding === "base64"
          ? Uint8Array.from(atob(file.content), (character) => character.charCodeAt(0))
          : new TextEncoder().encode(file.content);
      const url = URL.createObjectURL(new Blob([bytes], { type: file.mimeType }));
      const link = document.createElement("a");
      link.download = file.fileName;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportingFormat(null);
    }
  }

  async function copyViewLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      return;
    }
    setIsLinkCopied(true);
    if (linkCopiedTimeoutRef.current) {
      clearTimeout(linkCopiedTimeoutRef.current);
    }
    linkCopiedTimeoutRef.current = setTimeout(() => setIsLinkCopied(false), 1500);
  }

  const previousViewIdRef = useRef(view.id);
  useEffect(() => {
    setConfig(normalizeViewConfig(view.config));
    setViewFields(normalizeViewFields(view));
    setName(view.name);
    setType(view.type);
    // Only jump back to the root section when the user switches to a different
    // view — not on every optimistic patch to the current view. Resetting on
    // each edit bounced the user out of the Fields/Sort subpanel and unmounted
    // the drag-and-drop list mid-drop, which read as "changes don't apply" and
    // "the DnD is broken".
    if (previousViewIdRef.current !== view.id) {
      previousViewIdRef.current = view.id;
      setActiveSection("root");
    }
  }, [view]);

  useEffect(() => {
    if (!open) setActiveSection("root");
  }, [open]);

  const updateLocalConfig = (nextConfig: BoardViewConfig) => {
    setConfig(nextConfig);
  };

  const commitSorts = (sorts: BoardViewConfig["sorts"]) => {
    updateLocalConfig({ ...config, sorts });
    void onChangeSort({ boardId: board.id, boardViewId: view.id, sorts });
  };

  const commitGroup = (groupBy: BoardViewConfig["groupBy"], groupBucket: BoardViewConfig["groupBucket"]) => {
    updateLocalConfig({ ...config, groupBucket, groupBy });
    void onChangeGroup({ boardId: board.id, boardViewId: view.id, groupBucket, groupBy });
  };

  const commitFilter = async (filterExpression: string | null) => {
    await onChangeFilter({ boardId: board.id, boardViewId: view.id, filterExpression });
    setConfig((current) => ({ ...current, filterExpression }));
  };

  const commitLayout = (patch: Partial<BoardViewLayout>) => {
    const layout = { ...normalizeViewLayout(config.layout), ...patch };
    updateLocalConfig({ ...config, layout });
    void onChangeLayout({ boardId: board.id, boardViewId: view.id, layout: patch });
  };

  const commitVisibility = (includeClosedItems: boolean) => {
    updateLocalConfig({ ...config, includeClosedItems });
    void onChangeVisibility({ boardId: board.id, boardViewId: view.id, includeClosedItems });
  };

  const commitFields = (nextFields: BoardViewField[]) => {
    const nextConfig = ensureSortModeVisible(config, nextFields);
    updateLocalConfig(nextConfig);
    setViewFields(nextFields);
    void onChangeVisibleFields({
      boardId: board.id,
      boardViewId: view.id,
      fields: nextFields.map((field) => ({
        fieldDefinitionId: field.fieldDefinitionId,
        summaryOperator: field.summaryOperator ?? null,
        visible: field.visible,
      })),
    });
  };

  const commitType = (nextType: BoardViewType) => {
    const statusGroupBy = board.statusFieldDefinitionId ? (`field:${board.statusFieldDefinitionId}` as const) : null;
    const nextConfig = nextType === "kanban" && config.groupBy === null ? { ...config, groupBy: statusGroupBy } : config;
    setConfig(nextConfig);
    setType(nextType);
    void onChangeType({ boardId: board.id, boardViewId: view.id, type: nextType });
  };

  const sectionTitle = activeSection === "fields" ? t("board.fields") : activeSection === "filter" ? t("board.filter") : activeSection === "group" ? t("board.group") : activeSection === "layout" ? t("board.layout") : activeSection === "sort" ? t("board.sort") : "";

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        "absolute inset-y-0 right-0 z-20 flex min-h-[420px] w-full max-w-[340px] flex-col overflow-hidden bg-background shadow-2xl transition-[translate,opacity] duration-200 ease-out",
        open ? "pointer-events-auto translate-none opacity-100" : "pointer-events-none translate-x-full opacity-0",
      )}
    >
      <div className="flex items-center gap-2 border-b border-border/70 p-2.5">
        {activeSection === "root" ? (
          <>
            <div className="min-w-0 flex-1">
              <h2 className="text-[13px] font-semibold">{t("board.customizeView")}</h2>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{t("board.changesApply")}</p>
            </div>
            <Button aria-label={t("board.closeViewSettings")} onClick={() => onOpenChange(false)} size="icon-sm" variant="ghost">
              <X />
            </Button>
          </>
        ) : (
          <>
            <Button aria-label={t("board.back")} onClick={() => setActiveSection("root")} size="icon-sm" variant="ghost">
              <ChevronLeft />
            </Button>
            <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{sectionTitle}</h2>
            <Button aria-label={t("board.closeViewSettings")} onClick={() => onOpenChange(false)} size="icon-sm" variant="ghost">
              <X />
            </Button>
          </>
        )}
      </div>

      {activeSection === "root" ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <section className="px-2.5 py-2">
            <div className="grid grid-cols-[0.875rem_minmax(0,1fr)] items-center gap-2 px-2">
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      aria-label={t("board.changeViewType")}
                      className="-ml-[5px] size-6 rounded-md p-0 text-muted-foreground hover:text-foreground [&_svg]:!size-4"
                      disabled={disabled}
                      size="icon-sm"
                      variant="ghost"
                    >
                      {boardViewMeta[type].icon}
                    </Button>
                  }
                />
                <DropdownMenuContent align="start" className="w-40">
                  <DropdownMenuGroup>
                    {boardViewTypes.map((item) => (
                      <DropdownMenuItem
                        key={item}
                        onClick={() => {
                          if (item !== type) {
                            commitType(item);
                          }
                        }}
                      >
                        {boardViewMeta[item].icon}
                        <span className="min-w-0 flex-1">{t(boardViewMeta[item].labelKey)}</span>
                        {item === type ? <Check className="ml-auto text-primary" /> : null}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <EditableText
                ariaLabel={t("board.viewName")}
                className={cn("min-w-0 flex-1", disabled && "pointer-events-none opacity-50")}
                editTrigger="click"
                inputClassName="text-[13px] font-medium leading-none"
                onChange={(nextName) => {
                  const normalizedName = nextName.trim().replace(/\s+/g, " ");
                  if (normalizedName.length < 2) {
                    setName(view.name);
                    return;
                  }
                  setName(normalizedName);
                  if (normalizedName !== view.name) {
                    void onRename({ boardId: board.id, boardViewId: view.id, name: normalizedName });
                  }
                }}
                placeholder={t("board.viewName")}
                previewClassName="truncate text-[13px] font-medium leading-none"
                value={name}
              />
            </div>
          </section>

          <div className="px-2.5 py-1">
            {type !== "dashboard" ? (
              <SettingRow icon={<Eye />} label={t("board.fields")} onClick={() => setActiveSection("fields")} value={t("board.fieldsShown", { count: visibleFieldCount })} />
            ) : null}
            {type !== "dashboard" ? (
              <SettingRow icon={<Filter />} label={t("board.filter")} onClick={() => setActiveSection("filter")} value={config.filterExpression ? t("board.filtered") : t("board.none")} />
            ) : null}
            {supportsGrouping ? (
              <SettingRow icon={<Columns3 />} label={t("board.group")} onClick={() => setActiveSection("group")} value={config.groupBy ? groupByLabel(config.groupBy, fields) : t("board.noGrouping")} />
            ) : null}
            {supportsSorting ? (
              <SettingRow icon={<ArrowUpDown />} label={t("board.sort")} onClick={() => setActiveSection("sort")} value={config.sorts.length > 0 ? t("board.sortFields", { count: config.sorts.length }) : t("board.manual")} />
            ) : null}
            {type !== "form" && type !== "document" && type !== "dashboard" ? (
              <SettingRow
                icon={<LayoutGrid />}
                label={t("board.layout")}
                onClick={() => setActiveSection("layout")}
                value={viewLayoutLabels[type](normalizeViewLayout(config.layout), fields, t)}
              />
            ) : null}
          </div>

          <div className="px-2.5 py-1">
            {isPersonalView ? (
              <div className="flex items-start gap-2 rounded-md px-2 py-1.5 text-[11px] text-muted-foreground">
                <Lock className="mt-px size-3.5 shrink-0" />
                <span className="min-w-0">{t("board.personalViewBadge")}</span>
              </div>
            ) : (
              <SettingRow
                chevron={false}
                disabled={disabled || view.isDefault}
                icon={view.isDefault ? <Check /> : <Star />}
                label={t("board.setDefaultView")}
                onClick={() => {
                  void onSetDefault(view.id);
                }}
                value={view.isDefault ? t("board.defaultView") : undefined}
              />
            )}
            <SettingRow
              chevron={false}
              icon={isLinkCopied ? <Check /> : <Link />}
              label={t("board.copyLink")}
              onClick={() => {
                void copyViewLink();
              }}
              value={isLinkCopied ? t("board.copied") : undefined}
            />
            <SettingRow
              chevron={false}
              disabled={disabled}
              icon={<Copy />}
              label={t("board.duplicateView")}
              onClick={() => {
                void onDuplicate(view.id);
              }}
            />
            {([
              { format: "csv", label: t("board.exportCsv") },
              { format: "xlsx", label: t("board.exportExcel") },
              { format: "json", label: t("board.exportJson") },
            ] as const).map((option) => (
              <SettingRow
                chevron={false}
                disabled={disabled || exportingFormat !== null}
                icon={<Download />}
                key={option.format}
                label={option.label}
                onClick={() => {
                  void exportView(option.format);
                }}
                value={exportingFormat === option.format ? t("board.preparing") : undefined}
              />
            ))}
            <SettingRow
              chevron={false}
              destructive
              disabled={disabled || !canArchive}
              icon={<Trash2 />}
              label={t("board.archiveView")}
              onClick={() => setIsArchiveConfirmOpen(true)}
              value={canArchive ? undefined : t("board.lastView")}
            />
          </div>

          {type === "list" || type === "kanban" || type === "timeline" ? (
            <div className="px-2.5 pb-2.5">
              <ViewShareLinkControls viewId={view.id} />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
          {activeSection === "fields" ? (
            <FieldVisibilityList fieldStates={viewFields} fields={fields} lockedFieldIds={lockedFieldIds} onChange={commitFields} />
          ) : activeSection === "filter" ? (
            <ViewFilterEditor config={config} disabled={disabled} fields={fields} onCommit={commitFilter} onDone={() => setActiveSection("root")} />
          ) : activeSection === "sort" ? (
            <ViewSortControl config={config} disabled={disabled} fields={fields} onCommit={commitSorts} viewFields={viewFields} />
          ) : activeSection === "group" ? (
            <GroupOptionList
              config={config}
              disabled={disabled}
              fields={fields}
              groupValues={view.query.groups.map((group) => group.value)}
              onCommit={commitGroup}
            />
          ) : activeSection === "layout" ? (
            <ViewLayoutControl
              disabled={disabled}
              fields={fields}
              includeClosedItems={config.includeClosedItems !== false}
              layout={normalizeViewLayout(config.layout)}
              onCommit={commitLayout}
              onCommitVisibility={commitVisibility}
              type={type}
            />
          ) : null}
        </div>
      )}
      <ArchiveViewDialog
        disabled={disabled}
        onConfirm={async () => {
          await onArchive(view.id);
        }}
        onOpenChange={setIsArchiveConfirmOpen}
        open={isArchiveConfirmOpen}
        viewName={view.name}
      />
    </aside>
  );
}

function SettingRow({
  chevron = true,
  className,
  destructive,
  icon,
  label,
  value,
  ...props
}: { chevron?: boolean; destructive?: boolean; icon: ReactNode; label: string; value?: ReactNode } & ComponentProps<"button">) {
  const t = useT();
  return (
    <button
      className={cn(
        "flex h-7 w-full items-center gap-2 rounded-md px-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        destructive ? "text-destructive hover:bg-destructive/10" : "hover:bg-muted/50",
        className,
      )}
      type="button"
      {...props}
    >
      <span className={cn("grid size-3.5 shrink-0 place-items-center [&_svg]:size-3.5", destructive ? "text-destructive" : "text-muted-foreground")}>{icon}</span>
      <span className="min-w-0 flex-1 truncate text-xs font-medium">{label}</span>
      {value != null ? <span className="max-w-[46%] shrink-0 truncate text-[11px] text-muted-foreground">{value}</span> : null}
      {chevron ? <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" /> : null}
    </button>
  );
}

const sizeLabelKeys: Record<BoardViewLayout["cardSize"], string> = {
  compact: "board.sizeCompact",
  large: "board.sizeLarge",
  medium: "board.sizeDefault",
};

const viewLayoutLabels: Record<
  Extract<BoardViewType, "kanban" | "list" | "timeline">,
  (layout: BoardViewLayout, fields: WorkObjectFieldDefinition[], t: Translate) => string
> = {
  kanban: (layout, _fields, t) => t(sizeLabelKeys[layout.cardSize]),
  list: (layout, _fields, t) => t(sizeLabelKeys[layout.rowSize]),
  timeline: (layout, fields, t) => {
    const field = resolveTimelineDateField(fields, layout.timelineDateFieldId);
    return field ? field.name : t("board.chooseDateField");
  },
};

function ViewLayoutControl({
  disabled,
  fields,
  includeClosedItems,
  layout,
  onCommit,
  onCommitVisibility,
  type,
}: {
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  includeClosedItems: boolean;
  layout: BoardViewLayout;
  onCommit: (patch: Partial<BoardViewLayout>) => void;
  onCommitVisibility: (includeClosedItems: boolean) => void;
  type: BoardViewType;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-3">
      {type === "kanban" ? (
        <>
          <LayoutSizeControl
            disabled={disabled}
            label={t("board.cardSize")}
            onCommit={(size) => onCommit({ cardSize: size })}
            selected={layout.cardSize}
          />
          <div className="flex flex-col gap-0.5">
            <LayoutToggleRow
              checked={layout.showEmptyFields}
              description={t("board.showEmptyFieldsHint")}
              disabled={disabled}
              label={t("board.showEmptyFields")}
              onChange={(checked) => onCommit({ showEmptyFields: checked })}
            />
            <LayoutToggleRow
              checked={layout.showColumnColors}
              description={t("board.showColumnColorsHint")}
              disabled={disabled}
              label={t("board.showColumnColors")}
              onChange={(checked) => onCommit({ showColumnColors: checked })}
            />
            <LayoutToggleRow
              checked={layout.collapseEmptyColumns}
              description={t("board.collapseEmptyHint")}
              disabled={disabled}
              label={t("board.collapseEmpty")}
              onChange={(checked) => onCommit({ collapseEmptyColumns: checked })}
            />
          </div>
        </>
      ) : type === "list" ? (
        <>
          <LayoutSizeControl
            disabled={disabled}
            label={t("board.rowSize")}
            onCommit={(size) => onCommit({ rowSize: size })}
            selected={layout.rowSize}
          />
          <div className="flex flex-col gap-0.5">
            <LayoutToggleRow
              checked={layout.wrapText}
              description={t("board.wrapTextHint")}
              disabled={disabled}
              label={t("board.wrapText")}
              onChange={(checked) => onCommit({ wrapText: checked })}
            />
            <LayoutToggleRow
              checked={layout.pinFirstColumn}
              description={t("board.pinFirstColumnHint")}
              disabled={disabled}
              label={t("board.pinFirstColumn")}
              onChange={(checked) => onCommit({ pinFirstColumn: checked })}
            />
          </div>
        </>
      ) : type === "timeline" ? (
        <TimelineDateFieldControl
          disabled={disabled}
          fields={fields}
          onCommit={(timelineDateFieldId) => onCommit({ timelineDateFieldId })}
          selected={layout.timelineDateFieldId}
        />
      ) : null}
      <LayoutToggleRow
        checked={includeClosedItems}
        description={t("board.showClosedHint")}
        disabled={disabled}
        label={t("board.showClosed")}
        onChange={onCommitVisibility}
      />
    </div>
  );
}

function TimelineDateFieldControl({
  disabled,
  fields,
  onCommit,
  selected,
}: {
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  onCommit: (fieldId: string | null) => void;
  selected: string | null;
}) {
  const t = useT();
  const dateFields = fields.filter((field) => field.type === "date" || field.type === "date_range");
  const fallbackField = resolveTimelineDateField(fields, null);
  const autoLabel = fallbackField ? `Auto — ${fallbackField.name}` : "No date fields";
  const selectedValue = selected ?? "auto";

  return (
    <div>
      <p className="px-1 text-[11px] font-medium text-muted-foreground">{t("board.timelineDateField")}</p>
      <Select disabled={disabled || dateFields.length === 0} onValueChange={(value) => onCommit(value === "auto" ? null : value)} value={selectedValue}>
        <SelectTrigger aria-label={t("board.timelineDateField")} className="mt-1 w-full" size="sm">
          <SelectValue placeholder={t("board.chooseDateField")}>
            {(value: string | null) => value === "auto" ? autoLabel : dateFields.find((field) => field.id === value)?.name ?? t("board.chooseDateField")}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">{autoLabel}</SelectItem>
          {dateFields.map((field) => <SelectItem key={field.id} value={field.id}>{field.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <p className="mt-1 px-1 text-[10px] text-muted-foreground">{t("board.timelineDateFieldHint")}</p>
    </div>
  );
}

function LayoutSizeControl({
  disabled,
  label,
  onCommit,
  selected,
}: {
  disabled: boolean;
  label: string;
  onCommit: (size: BoardViewLayout["cardSize"]) => void;
  selected: BoardViewLayout["cardSize"];
}) {
  const t = useT();
  return (
    <div>
      <p className="px-1 text-[11px] font-medium text-muted-foreground">{label}</p>
      <div className="mt-1 grid grid-cols-3 gap-0.5 rounded-lg bg-muted/30 p-0.5">
        {(["compact", "medium", "large"] as const).map((size) => (
          <button
            className={cn(
              "h-7 rounded-md text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
              selected === size ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
            )}
            disabled={disabled}
            key={size}
            onClick={() => onCommit(size)}
            type="button"
          >
            {t(sizeLabelKeys[size])}
          </button>
        ))}
      </div>
    </div>
  );
}

function LayoutToggleRow({
  checked,
  description,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  description: string;
  disabled: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      aria-checked={checked}
      className="flex w-full items-center justify-between gap-2 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      role="switch"
      type="button"
    >
      <span className="min-w-0">
        <span className="block text-[13px]">{label}</span>
        <span className="mt-0 block text-[11px] text-muted-foreground/70">{description}</span>
      </span>
      <span className={cn("flex h-4 w-8 shrink-0 items-center rounded-full p-0.5 transition-colors", checked ? "bg-primary" : "bg-muted")}>
        <span className={cn("size-3 rounded-full bg-background shadow-xs transition-transform", checked && "translate-x-4")} />
      </span>
    </button>
  );
}

function GroupOptionList({
  config,
  disabled,
  fields,
  groupValues,
  onCommit,
}: {
  config: BoardViewConfig;
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  groupValues: string[];
  onCommit: (groupBy: BoardViewConfig["groupBy"], groupBucket: BoardViewConfig["groupBucket"]) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const groupableFields = fields.filter((field) => isGroupableFieldType(field.type));
  const options = [{ label: t("board.noGrouping"), value: "none" }, ...groupableFields.map((field) => ({ label: field.name, value: `field:${field.id}` }))];
  const current = fromGroupBy(config.groupBy);
  const bucketKind = config.groupBy ? groupBucketKindForValues(groupValues) : null;
  const currentBucket = groupBucketValue(config.groupBucket ?? null);
  const bucketOptions = bucketKind
    ? [
        { label: t("board.groupBucketExact"), value: "exact" },
        ...(bucketKind === "date"
          ? groupBucketUnits.map((unit) => ({ label: t(`board.groupBucketUnit.${unit}`), value: `date:${unit}` }))
          : suggestNumberBucketSizes(groupValues).map((size) => ({
              label: t("board.groupBucketStep", { size: size.toLocaleString(locale) }),
              value: `number:${size}`,
            }))),
      ]
    : [];
  return (
    <div className="flex flex-col gap-0.5">
      {options.map((option) => (
        <button
          className="flex h-8 items-center justify-between gap-2 rounded-md px-1.5 text-left text-[13px] transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          key={option.value}
          onClick={() => onCommit(toGroupBy(option.value), null)}
          type="button"
        >
          <span className="min-w-0 truncate">{option.label}</span>
          {current === option.value ? <Check className="size-4 shrink-0 text-primary" /> : null}
        </button>
      ))}
      {bucketOptions.length > 0 ? (
        <>
          <p className="mt-2 px-1.5 text-xs font-medium text-muted-foreground">{t("board.groupBucket")}</p>
          {bucketOptions.map((option) => (
            <button
              className="flex h-8 items-center justify-between gap-2 rounded-md px-1.5 text-left text-[13px] transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={disabled}
              key={option.value}
              onClick={() => onCommit(config.groupBy, parseGroupBucketValue(option.value))}
              type="button"
            >
              <span className="min-w-0 truncate">{option.label}</span>
              {currentBucket === option.value ? <Check className="size-4 shrink-0 text-primary" /> : null}
            </button>
          ))}
        </>
      ) : null}
    </div>
  );
}

function ViewControlChip({ active, className, icon, label, ...props }: { active: boolean; icon: ReactNode; label: string } & ComponentProps<typeof Button>) {
  return (
    <Button
      className={cn("gap-1.5", active && "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary", className)}
      size="sm"
      variant="outline"
      {...props}
    >
      <span className="[&_svg]:size-3.5">{icon}</span>
      <span className="max-w-40 truncate">{label}</span>
    </Button>
  );
}

function ViewGroupControl({
  config,
  disabled,
  fields,
  groupValues,
  onCommit,
}: {
  config: BoardViewConfig;
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  // Значения групп, которые уже вернул сервер: по ним видно, диапазон это дат
  // или чисел. Сама ширина корзины из них не выводится — только выбор того,
  // какой переключатель показать.
  groupValues: string[];
  onCommit: (groupBy: BoardViewConfig["groupBy"], groupBucket: BoardViewConfig["groupBucket"]) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const groupableFields = fields.filter((field) => isGroupableFieldType(field.type));
  const bucketKind = config.groupBy ? groupBucketKindForValues(groupValues) : null;
  const bucket = config.groupBucket ?? null;
  const sizes = suggestNumberBucketSizes(groupValues);
  return (
    <div className="space-y-2">
      <Select
        value={fromGroupBy(config.groupBy)}
        onValueChange={(nextValue) => {
          // Поле сменилось — прежняя ширина к нему не относится.
          if (nextValue !== null) onCommit(toGroupBy(nextValue), null);
        }}
      >
        <SelectTrigger className="w-full" disabled={disabled}>
          <span className="min-w-0 flex-1 truncate text-left">{groupByLabel(config.groupBy, fields)}</span>
        </SelectTrigger>
        <SelectContent align="start" alignItemWithTrigger={false}>
          <SelectGroup>
            <SelectItem value="none">{t("board.noGrouping")}</SelectItem>
            {groupableFields.map((field) => (
              <SelectItem key={field.id} value={`field:${field.id}`}>{field.name}</SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>

      {bucketKind ? (
        <div className="space-y-1">
          <p className="px-0.5 text-xs font-medium text-muted-foreground">{t("board.groupBucket")}</p>
          <Select
            value={groupBucketValue(bucket)}
            onValueChange={(nextValue) => {
              if (nextValue !== null) onCommit(config.groupBy, parseGroupBucketValue(nextValue));
            }}
          >
            <SelectTrigger className="w-full" disabled={disabled}>
              <span className="min-w-0 flex-1 truncate text-left">
                {bucket
                  ? bucket.kind === "date"
                    ? t(`board.groupBucketUnit.${bucket.unit}`)
                    : t("board.groupBucketStep", { size: bucket.size.toLocaleString(locale) })
                  : t("board.groupBucketExact")}
              </span>
            </SelectTrigger>
            <SelectContent align="start" alignItemWithTrigger={false}>
              <SelectGroup>
                <SelectItem value="exact">{t("board.groupBucketExact")}</SelectItem>
                {bucketKind === "date"
                  ? groupBucketUnits.map((unit) => (
                      <SelectItem key={unit} value={`date:${unit}`}>{t(`board.groupBucketUnit.${unit}`)}</SelectItem>
                    ))
                  : sizes.map((size) => (
                      <SelectItem key={size} value={`number:${size}`}>
                        {t("board.groupBucketStep", { size: size.toLocaleString(locale) })}
                      </SelectItem>
                    ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </div>
  );
}

function groupBucketValue(bucket: BoardViewConfig["groupBucket"]) {
  if (!bucket) return "exact";
  return bucket.kind === "date" ? `date:${bucket.unit}` : `number:${bucket.size}`;
}

function parseGroupBucketValue(value: string): BoardViewConfig["groupBucket"] {
  if (value.startsWith("date:")) {
    const unit = value.slice("date:".length) as (typeof groupBucketUnits)[number];
    return groupBucketUnits.includes(unit) ? { kind: "date", unit } : null;
  }
  if (value.startsWith("number:")) {
    const size = Number(value.slice("number:".length));
    return Number.isFinite(size) && size > 0 ? { kind: "number", size } : null;
  }
  return null;
}

function ViewSortControl({ config, disabled, fields, onCommit, viewFields }: { config: BoardViewConfig; disabled: boolean; fields: WorkObjectFieldDefinition[]; onCommit: (sorts: BoardViewConfig["sorts"]) => void; viewFields: BoardViewField[] }) {
  const t = useT();
  const sortableFields = visibleFieldsForView(viewFields, fields);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{t("board.sort")}</span>
        <div className="flex items-center gap-1">
          {config.sorts.length > 0 ? (
            <Button disabled={disabled} onClick={() => onCommit([])} size="xs" variant="ghost">{t("board.manual")}</Button>
          ) : null}
          <Button
            disabled={disabled || config.sorts.length >= 5}
            onClick={() => {
              const selected = new Set(config.sorts.map((sort) => sort.field));
              const field = ["title", ...sortableFields.map((item) => `field:${item.id}`)]
                .find((candidate) => !selected.has(candidate as BoardViewConfig["sorts"][number]["field"]));
              if (field) onCommit([...config.sorts, { direction: "asc", field: field as BoardViewConfig["sorts"][number]["field"], nulls: "last" }]);
            }}
            size="xs"
            variant="outline"
          >
            <Plus /> {t("board.addSort")}
          </Button>
        </div>
      </div>
      {config.sorts.length === 0 ? (
        <div className="rounded-md border border-dashed px-2.5 py-2 text-xs text-muted-foreground">{t("board.manualOrder")}</div>
      ) : (
        <div className="space-y-1.5">
          {config.sorts.map((sort, index) => (
            <div className="grid grid-cols-[minmax(0,1fr)_4.75rem_5.75rem_auto] gap-1.5" key={sort.field}>
              <Select
                value={sort.field}
                onValueChange={(value) => {
                  if (!value) return;
                  const next = [...config.sorts];
                  next[index] = { ...sort, field: value as typeof sort.field };
                  onCommit(next);
                }}
              >
                <SelectTrigger className="w-full" disabled={disabled}>
                  <span className="min-w-0 flex-1 truncate text-left">{sortFieldLabel(sort.field, fields)}</span>
                </SelectTrigger>
                <SelectContent align="start" alignItemWithTrigger={false}>
                  <SelectGroup>
                    <SelectItem disabled={config.sorts.some((item, itemIndex) => itemIndex !== index && item.field === "title")} value="title">{t("board.name")}</SelectItem>
                    {sortableFields.map((field) => {
                      const value = `field:${field.id}` as const;
                      return <SelectItem disabled={config.sorts.some((item, itemIndex) => itemIndex !== index && item.field === value)} key={field.id} value={value}>{field.name}</SelectItem>;
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Button
                aria-label={t(sort.direction === "asc" ? "a11y.sortDescending" : "a11y.sortAscending")}
                disabled={disabled}
                onClick={() => {
                  const next = [...config.sorts];
                  next[index] = { ...sort, direction: sort.direction === "asc" ? "desc" : "asc" };
                  onCommit(next);
                }}
                size="sm"
                variant="outline"
              >
                {sort.direction === "asc" ? <ChevronUp /> : <ChevronDown />}
                {sort.direction === "asc" ? "Asc" : "Desc"}
              </Button>
              <Select
                value={sort.nulls}
                onValueChange={(value) => {
                  if (!value) return;
                  const next = [...config.sorts];
                  next[index] = { ...sort, nulls: value as typeof sort.nulls };
                  onCommit(next);
                }}
              >
                <SelectTrigger className="w-full" disabled={disabled}>
                  <span className="truncate">{sort.nulls === "first" ? t("board.emptyFirst") : t("board.emptyLast")}</span>
                </SelectTrigger>
                <SelectContent align="end" alignItemWithTrigger={false}>
                  <SelectItem value="first">{t("board.emptyFirst")}</SelectItem>
                  <SelectItem value="last">{t("board.emptyLast")}</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center">
                <Button
                  aria-label={t("board.increaseSort")}
                  disabled={disabled || index === 0}
                  onClick={() => {
                    const next = [...config.sorts];
                    [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
                    onCommit(next);
                  }}
                  size="icon-sm"
                  variant="ghost"
                ><ChevronUp /></Button>
                <Button
                  aria-label={t("board.decreaseSort")}
                  disabled={disabled || index === config.sorts.length - 1}
                  onClick={() => {
                    const next = [...config.sorts];
                    [next[index], next[index + 1]] = [next[index + 1]!, next[index]!];
                    onCommit(next);
                  }}
                  size="icon-sm"
                  variant="ghost"
                ><ChevronDown /></Button>
                <Button aria-label={t("board.removeSort")} disabled={disabled} onClick={() => onCommit(config.sorts.filter((_, itemIndex) => itemIndex !== index))} size="icon-sm" variant="ghost">
                  <Trash2 />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ViewFilterEditor({
  config,
  disabled,
  fields,
  onCommit,
  onDone,
}: {
  config: BoardViewConfig;
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  onCommit: (filterExpression: string | null) => Promise<void>;
  onDone: () => void;
}) {
  const t = useT();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [state, setState] = useState<PredicateBuilderState>({ expression: "", hasIncomplete: false, isEmpty: true, isReady: true, mode: "visual" });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const hadFilter = Boolean(config.filterExpression);

  async function apply() {
    setIsSaving(true);
    setError(null);
    try {
      if (state.isEmpty) {
        await onCommit(null);
        onDone();
        return;
      }
      const canonical = (await queryClient.fetchQuery(trpc.boards.inspectStatusRule.queryOptions({ expression: state.expression }))).canonicalExpression;
      await onCommit(canonical);
      onDone();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not apply this filter.");
    } finally {
      setIsSaving(false);
    }
  }

  const applyDisabled =
    disabled ||
    isSaving ||
    (state.isEmpty && !hadFilter) ||
    (!state.isEmpty && state.mode === "visual" && (!state.isReady || state.hasIncomplete));

  return (
    <div className="flex flex-col gap-3">
      <VisualPredicateBuilder
        disabled={disabled || isSaving}
        emptyHint={t("filterBuilder.emptyHintView")}
        externalError={error}
        fields={fields}
        initialExpression={config.filterExpression ?? ""}
        onStateChange={setState}
        resetSignal={0}
      />
      {error ? <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert> : null}
      <div className="flex items-center justify-end gap-2 border-t border-border/60 pt-3">
        <Button disabled={applyDisabled} onClick={() => void apply()} size="sm" variant={state.isEmpty && hadFilter ? "outline" : "default"} type="button">
          {isSaving ? t("fieldDialog.saving") : state.isEmpty && hadFilter ? t("board.clearFilter") : t("board.applyFilter")}
        </Button>
      </div>
    </div>
  );
}

function FilterEditorPopover({
  align = "end",
  config,
  disabled,
  fields,
  onCommit,
  trigger,
}: {
  align?: "center" | "end" | "start";
  config: BoardViewConfig;
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  onCommit: (filterExpression: string | null) => Promise<void>;
  trigger: ReactElement;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={trigger} />
      <PopoverContent align={align} className="max-h-[70vh] w-[36rem] max-w-[calc(100vw-2rem)] overflow-y-auto p-3.5">
        <ViewFilterEditor config={config} disabled={disabled} fields={fields} onCommit={onCommit} onDone={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

function FieldVisibilityList({
  fieldStates,
  fields,
  lockedFieldIds,
  onChange,
}: {
  fieldStates: BoardViewField[];
  fields: WorkObjectFieldDefinition[];
  lockedFieldIds: string[];
  onChange: (fields: BoardViewField[]) => void;
}) {
  const t = useT();
  const lockedIds = new Set(lockedFieldIds);
  const fieldsById = new Map(fields.map((field) => [field.id, field]));
  const fieldStatesById = new Map(fieldStates.map((field) => [field.fieldDefinitionId, field]));
  const orderedFieldStates = [
    ...fieldStates.filter((field) => fieldsById.has(field.fieldDefinitionId)),
    ...fields
      .filter((field) => !fieldStatesById.has(field.id))
      .map((field, index) => ({
        fieldDefinitionId: field.id,
        locked: lockedIds.has(field.id),
        rank: optimisticFieldRank(fieldStates.length + index),
        summary: null,
        summaries: [],
        summaryOperator: null,
        visible: lockedIds.has(field.id) ? true : false,
      })),
  ];

  function reorderFields(orderedItemIds: string[]) {
    const fieldStateById = new Map(orderedFieldStates.map((field) => [field.fieldDefinitionId, field]));
    const reordered = orderedItemIds
      .map((id) => fieldStateById.get(id))
      .filter((field): field is (typeof orderedFieldStates)[number] => Boolean(field))
      .map((field, index) => ({
        ...field,
        rank: optimisticFieldRank(index),
        summary: field.summary ?? null,
        summaries: field.summaries ?? [],
        summaryOperator: lockedIds.has(field.fieldDefinitionId) || !field.visible ? null : field.summaryOperator ?? null,
        visible: lockedIds.has(field.fieldDefinitionId) ? true : field.visible,
      }));
    onChange(reordered);
  }

  return (
    <div className="flex flex-col gap-1">
      {fields.length > 0 ? (
        <DndSortableVerticalList
          itemIds={orderedFieldStates.map((fieldState) => fieldState.fieldDefinitionId)}
          onReorder={({ orderedItemIds }) => reorderFields(orderedItemIds)}
        >
          <div className="flex flex-col gap-1">
            {orderedFieldStates.map((fieldState) => {
              const field = fieldsById.get(fieldState.fieldDefinitionId);
              if (!field) {
                return null;
              }
              return (
                <FieldVisibilityRow
                  field={field}
                  fieldState={fieldState}
                  isLocked={lockedIds.has(field.id)}
                  isVisible={fieldState.visible}
                  key={field.id}
                  onSummaryOperatorChange={(summaryOperator) =>
                    onChange(
                      orderedFieldStates.map((item) =>
                        item.fieldDefinitionId === field.id
                          ? {
                              ...item,
                              summaryOperator: lockedIds.has(field.id) || !item.visible ? null : summaryOperator,
                            }
                          : item,
                      ),
                    )
                  }
                  onToggle={() =>
                    onChange(
                      orderedFieldStates.map((item) =>
                        item.fieldDefinitionId === field.id
                          ? {
                              ...item,
                              summaryOperator: lockedIds.has(field.id) || item.visible ? null : item.summaryOperator ?? null,
                              visible: lockedIds.has(field.id) ? true : !item.visible,
                            }
                          : item,
                      ),
                    )
                  }
                />
              );
            })}
          </div>
        </DndSortableVerticalList>
      ) : (
        <p className="text-xs text-muted-foreground">{t("board.noFields")}</p>
      )}
    </div>
  );
}

// Dashboard views host cross-board widgets; the board picker offers every
// board the workspace query returns.
function DashboardViewSurface({
  board,
  fields,
  viewId,
}: {
  board: BoardView;
  fields: WorkObjectFieldDefinition[];
  viewId: string;
}) {
  const boards = useBoards();
  const boardOptions = (boards.data?.boards ?? []).map((entry) => ({ id: entry.id, name: entry.name }));
  return (
    <DashboardWidgets
      boards={boardOptions.length > 0 ? boardOptions : [{ id: board.id, name: board.name }]}
      fields={fields}
      ownBoardId={board.id}
      viewId={viewId}
    />
  );
}

function FieldVisibilityRow({
  field,
  fieldState,
  isLocked,
  isVisible,
  onSummaryOperatorChange,
  onToggle,
}: {
  field: WorkObjectFieldDefinition;
  fieldState: BoardViewField;
  isLocked: boolean;
  isVisible: boolean;
  onSummaryOperatorChange: (operator: BoardViewFieldSummaryOperator | null) => void;
  onToggle: () => void;
}) {
  const t = useT();
  const summaryOperators = summaryOperatorsForField(field);
  const sortable = useDndSortableItem(field.id, { disabled: isLocked });

  return (
    <div
      className={cn(
        "group/field-row flex h-7 w-full items-center gap-1.5 rounded-md px-1.5 text-xs transition-colors",
        !isLocked && "cursor-grab touch-none select-none active:cursor-grabbing",
        isLocked ? "text-muted-foreground" : "hover:bg-muted/50",
        !isLocked && (isVisible ? "text-foreground" : "text-muted-foreground"),
        sortable.isDragSource && "relative z-10 bg-muted/70 opacity-80 shadow-sm",
      )}
      ref={sortable.setNodeRef}
      {...sortable.dragProps}
      style={sortable.style}
    >
      <button
        aria-label={isLocked ? `${field.name} is always visible` : `${isVisible ? t("board.hide") : t("board.show")} ${field.name}`}
        aria-pressed={isVisible}
        className="grid size-5 shrink-0 cursor-pointer place-items-center rounded-sm transition-colors disabled:cursor-default disabled:opacity-70 [&_svg]:size-3.5"
        disabled={isLocked}
        onClick={onToggle}
        onPointerDown={(event) => event.stopPropagation()}
        type="button"
      >
        <span
          className={cn(
            "grid size-5 place-items-center rounded-sm transition-colors",
            isVisible ? "bg-primary/15 text-primary" : "bg-muted/35 text-muted-foreground",
            isLocked && "bg-muted/25 text-muted-foreground",
          )}
        >
          {isVisible ? <Eye /> : <EyeOff />}
        </span>
      </button>
      <span className="min-w-0 flex-1 truncate">{field.name}</span>
      {!isLocked && isVisible ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <button
                aria-label={t("a11y.calculateField", { field: field.name })}
                className={cn(
                  "inline-flex h-5 shrink-0 items-center gap-1 rounded-sm px-1.5 text-[11px] text-muted-foreground opacity-0 transition-opacity hover:bg-muted/70 hover:text-foreground group-hover/field-row:opacity-100 focus-visible:opacity-100",
                  fieldState.summaryOperator && "opacity-100 text-foreground",
                )}
                onPointerDown={(event) => event.stopPropagation()}
                type="button"
              >
                <Sigma className="size-3.5" />
                {fieldState.summaryOperator ? summaryOperatorLabels[fieldState.summaryOperator] : t("board.calculate")}
              </button>
            }
          />
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  onSummaryOperatorChange(null);
                }}
              >
                {t("board.noCalculation")}
              </DropdownMenuItem>
              {summaryOperators.map((operator) => (
                <DropdownMenuItem
                  key={operator}
                  onClick={() => {
                    onSummaryOperatorChange(operator);
                  }}
                >
                  <span className="min-w-0 flex-1 truncate">{summaryOperatorLabels[operator]}</span>
                  {fieldState.summaryOperator === operator ? <Check className="size-4" /> : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {/* Decorative handle only — the whole row is the drag handle (see the row's
          dragHandleProps above), matching the kanban card. */}
      <span
        aria-hidden
        className={cn(
          "grid size-5 shrink-0 place-items-center rounded-sm text-muted-foreground opacity-0 transition-opacity group-hover/field-row:opacity-100 [&_svg]:size-3.5",
          isLocked ? "opacity-0" : "opacity-60",
        )}
      >
        <GripVertical />
      </span>
    </div>
  );
}

function BoardViewSurface({
  board,
  disabled,
  fields,
  onArchive,
  onCreateDefaultView,
  onCreateWork,
  onCreateWorkInline,
  onMoveOrder,
  onMoveToGroup,
  loadingViewPageKey,
  onLoadNextViewPage,
  onOpenItem,
  onReplaceOrder,
  onChangeSort,
  onChangeVisibleFields,
  onUpdate,
  onUpdateFieldValues,
  onUpdateFormulaField,
  onUpdateType,
  sprintScope,
  sprintTrackBoardId,
  types,
  users,
  view,
}: {
  board: BoardView;
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  onArchive: (input: { boardItemId: string }) => Promise<void>;
  onCreateDefaultView: () => Promise<void>;
  onCreateWork: () => void;
  sprintScope?: string | null;
  sprintTrackBoardId?: string | null;
  onCreateWorkInline: (
    input: CreateWorkObjectInput,
    options?: {
      boardStatusId?: string;
      groupKey?: string;
      orderedWorkObjectIds?: string[];
      targetGroupValue?: string;
      viewId?: string;
    },
  ) => Promise<void>;
  onMoveOrder: (input: {
    boardId: string;
    boardViewId: string;
    groupKey: string;
    nextWorkObjectId?: string | null;
    orderedWorkObjectIds: string[];
    previousWorkObjectId?: string | null;
    workObjectId: string;
  }) => Promise<void>;
  onMoveToGroup: (input: {
    boardId: string;
    boardItemId: string;
    boardViewId: string;
    groupKey: string;
    nextWorkObjectId?: string | null;
    orderedWorkObjectIds: string[];
    previousWorkObjectId?: string | null;
    targetGroupValue: string;
    workObjectId: string;
  }) => Promise<void>;
  loadingViewPageKey: string | null;
  onLoadNextViewPage: (input: { boardId: string; boardViewId: string; groupValue?: string }) => Promise<void>;
  onOpenItem: (item: BoardItemView) => void;
  onReplaceOrder: (input: { boardId: string; boardViewId: string; groupKey: string; workObjectIds: string[] }) => Promise<void>;
  onChangeSort: (input: { boardId: string; boardViewId: string; sorts: BoardViewConfig["sorts"] }) => Promise<void>;
  onChangeVisibleFields: (input: { boardId: string; boardViewId: string; fields: Array<{ fieldDefinitionId: string; summaryOperator?: BoardViewField["summaryOperator"]; visible: boolean }> }) => Promise<void>;
  onUpdate: (input: { boardItemId: string; boardStatusId: string }) => Promise<void>;
  onUpdateFieldValues: (input: UpdateWorkObjectFieldValuesInput) => Promise<void>;
  onUpdateFormulaField: (field: WorkObjectFieldDefinition, expression: string) => Promise<void>;
  onUpdateType: (input: { type: NonNullable<BoardItemView["workObject"]["type"]>; workObjectId: string }) => Promise<void>;
  types: WorkObjectListView["types"];
  users: WorkObjectListView["users"];
  view: BoardView["views"][number] | null;
}) {
  const t = useT();
  if (!view) {
    return (
      <Empty className="min-h-[360px] justify-center">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Settings2 />
          </EmptyMedia>
          <EmptyTitle>{t("board.noActiveViews")}</EmptyTitle>
          <EmptyDescription>{t("board.repairViewHint")}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button
            onClick={() => {
              void onCreateDefaultView();
            }}
          >
            {t("board.repairView")}
          </Button>
        </EmptyContent>
      </Empty>
    );
  }

  if (view.type === "kanban") {
    return (
      <KanbanBoardView
        board={board}
        disabled={disabled}
        fields={fields}
        sprintTrackBoardId={sprintTrackBoardId}
        onArchive={onArchive}
        onCreateWorkInline={onCreateWorkInline}
        onMoveOrder={onMoveOrder}
        onMoveToGroup={onMoveToGroup}
        loadingViewPageKey={loadingViewPageKey}
        onLoadNextViewPage={onLoadNextViewPage}
        onOpenItem={onOpenItem}
        onReplaceOrder={onReplaceOrder}
        onChangeSort={onChangeSort}
        onChangeVisibleFields={onChangeVisibleFields}
        onUpdate={onUpdate}
        onUpdateFieldValues={onUpdateFieldValues}
        onUpdateFormulaField={onUpdateFormulaField}
        onUpdateType={onUpdateType}
        types={types}
        users={users}
        view={view}
      />
    );
  }

  if (view.type === "timeline") {
    return (
      <TimelineBoardView
        board={board}
        disabled={disabled}
        fields={fields}
        sprintScope={sprintScope}
        onArchive={onArchive}
        onCreateWorkInline={onCreateWorkInline}
        onMoveOrder={onMoveOrder}
        onMoveToGroup={onMoveToGroup}
        onOpenItem={onOpenItem}
        onReplaceOrder={onReplaceOrder}
        onChangeSort={onChangeSort}
        onChangeVisibleFields={onChangeVisibleFields}
        onUpdate={onUpdate}
        onUpdateFieldValues={onUpdateFieldValues}
        onUpdateFormulaField={onUpdateFormulaField}
        onUpdateType={onUpdateType}
        types={types}
        users={users}
        view={view}
      />
    );
  }

  if (view.type === "document") {
    return <DocumentBoardView disabled={disabled} key={view.id} viewId={view.id} />;
  }

  if (view.type === "form") {
    return (
      <FormViewSurface
        fields={fields}
        statusFieldDefinitionId={board.statusFieldDefinitionId}
        onCreate={async (input) => {
          const typeId = types[0]?.id;
          if (!typeId) {
            throw new Error( t("board.createTypeFirst"));
          }
          await onCreateWorkInline({ title: input.title, typeId, values: input.values }, { viewId: view.id });
        }}
        view={view}
      />
    );
  }

  if (view.type === "dashboard") {
    return <DashboardViewSurface board={board} fields={fields} key={view.id} viewId={view.id} />;
  }

  return (
    <ListBoardView
      board={board}
      disabled={disabled}
      fields={fields}
      sprintTrackBoardId={sprintTrackBoardId}
      onArchive={onArchive}
      onCreateWork={onCreateWork}
      onCreateWorkInline={onCreateWorkInline}
      onMoveOrder={onMoveOrder}
      onMoveToGroup={onMoveToGroup}
      onOpenItem={onOpenItem}
      onReplaceOrder={onReplaceOrder}
      onChangeSort={onChangeSort}
      onChangeVisibleFields={onChangeVisibleFields}
      onUpdate={onUpdate}
      onUpdateFieldValues={onUpdateFieldValues}
      onUpdateFormulaField={onUpdateFormulaField}
      onUpdateType={onUpdateType}
      types={types}
      users={users}
      view={view}
    />
  );
}

function ListBoardView(
  props: BoardItemsProps & {
    onCreateWork: () => void;
    onCreateWorkInline: (
      input: CreateWorkObjectInput,
      options?: {
        boardStatusId?: string;
        groupKey?: string;
        orderedWorkObjectIds?: string[];
        targetGroupValue?: string;
        viewId?: string;
      },
    ) => Promise<void>;
  },
) {
  const { locale } = useI18n();
  const t = useT();
  const { board, fields, view } = props;
  const [creatingGroupKey, setCreatingGroupKey] = useState<string | null>(null);
  const [inlineTitle, setInlineTitle] = useState("");
  const [isInlineCreating, setIsInlineCreating] = useState(false);
  const [itemsToRemove, setItemsToRemove] = useState<BoardItemView[]>([]);
  const layout = normalizeViewLayout(view.config.layout);
  const groups = buildBoardItemGroups(board, view, fields, t, locale, props.users);
  useRepairManualViewOrder({ board, disabled: props.disabled, groups, onReplaceOrder: props.onReplaceOrder, view });
  const visibleFieldIds = visibleFieldIdsForView(view);
  const fieldById = new Map(fields.map((field) => [field.id, field]));
  const visibleColumnFields = visibleFieldIds
    .map((fieldId) => fieldById.get(fieldId))
    .filter((field): field is WorkObjectFieldDefinition => Boolean(field))
    .filter((field) => field.key !== "title");
  const viewFieldByFieldId = new Map(view.fields.map((field) => [field.fieldDefinitionId, field]));
  // The Σ footer rows only exist once somebody configured a calculation —
  // an all-"Calculate" row under every group is noise. The first calculation
  // is set from the view settings panel's per-field Calculate control.
  const hasAnySummary = view.fields.some((field) => field.visible && field.summaryOperator);
  const canDragOrder = normalizeViewConfig(view.config).sorts.length === 0;
  const groupByWorkObjectId = new Map(groups.flatMap((group) => group.items.map((item) => [item.workObject.id, group])));
  const data = groups.flatMap((group) => group.items);
  const columns = useMemo<ColumnDef<BoardItemView>[]>(
    () => [
      {
        id: "name",
        accessorFn: (item) => item.workObject.title,
        enableHiding: false,
        enableSorting: true,
        header: t("board.name"),
        minSize: 220,
        size: 460,
        meta: {
          cellClassName: "min-w-[220px]",
          headerClassName: "min-w-[220px]",
          locked: true,
        },
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-2">
            {row.original.workObject.itemKey ? (
              <span className="shrink-0 font-mono text-[11px] text-muted-foreground/60">{row.original.workObject.itemKey}</span>
            ) : null}
            <button
              className={cn(
                "min-w-0 rounded-sm text-left text-sm font-medium text-foreground/90 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                layout.wrapText ? "whitespace-normal break-words" : "truncate",
              )}
              onClick={() => props.onOpenItem(row.original)}
              type="button"
            >
              {row.original.workObject.title}
            </button>
          </div>
        ),
      },
      ...visibleColumnFields.map((field): ColumnDef<BoardItemView> => {
        if (field.key === "type") {
          return {
            id: "type",
            accessorFn: (item) => item.workObject.type?.name ?? "",
            enableSorting: true,
            header: t("table.type"),
            minSize: 96,
            size: 116,
            cell: ({ row }) => (
              <BoardTypeSelect
                disabled={props.disabled}
                item={row.original}
                onUpdate={props.onUpdateType}
                types={props.types}
              />
            ),
          };
        }
        if (isBoardStatusField(board, field)) {
          return {
            id: `field:${field.id}`,
            accessorFn: (item) => item.status.name,
            enableSorting: true,
            header: field.name,
            minSize: 112,
            size: 136,
            cell: ({ row }) => (
              <div className="flex min-w-0 items-center gap-2">
                <BoardStatusSelect board={board} disabled={props.disabled} item={row.original} onOpenItem={props.onOpenItem} onUpdate={props.onUpdate} />
                <ReplayStatusSinceBadge workObjectId={row.original.workObject.id} />
              </div>
            ),
          };
        }

        return {
          id: `field:${field.id}`,
          accessorFn: (item) => (fieldAppliesToType(field, item.workObject.type?.id) ? fieldValueLabel(item, field) : ""),
          enableSorting: true,
          header: field.name,
          minSize: field.type === "rating" ? 112 : field.type === "duration" ? 116 : 140,
          size: field.type === "rating" ? 144 : field.type === "duration" ? 144 : 180,
          cell: ({ row }) => (
            <BoardFieldValueCell
              disabled={props.disabled}
              field={field}
              fields={fields}
              item={row.original}
              onUpdate={props.onUpdateFieldValues}
              onUpdateFormulaField={props.onUpdateFormulaField}
            />
          ),
        };
      }),
    ],
    [board, fields, props.disabled, props.onOpenItem, props.onUpdate, props.onUpdateFieldValues, props.onUpdateFormulaField, props.onUpdateType, props.types, visibleColumnFields],
  );

  async function removeFromBoard(items: BoardItemView[]) {
    for (const { id: boardItemId } of items) {
      await props.onArchive({ boardItemId });
    }
  }

  async function moveRow(input: {
    activeItem: BoardItemView;
    nextItem: BoardItemView | null;
    previousItem: BoardItemView | null;
    targetGroupKey: string;
  }) {
    const { activeItem, nextItem, previousItem, targetGroupKey } = input;
    const sourceGroup = groupByWorkObjectId.get(activeItem.workObject.id);
    const targetGroup = groups.find((group) => group.key === targetGroupKey);
    if (!sourceGroup || !targetGroup) {
      return;
    }
    const targetItemsWithoutActive = targetGroup.items.filter((item) => item.id !== activeItem.id);
    const previousIndex = previousItem ? targetItemsWithoutActive.findIndex((item) => item.id === previousItem.id) : -1;
    const nextIndex = nextItem ? targetItemsWithoutActive.findIndex((item) => item.id === nextItem.id) : -1;
    const insertIndex = previousIndex >= 0 ? previousIndex + 1 : nextIndex >= 0 ? nextIndex : targetItemsWithoutActive.length;
    const nextItems = [
      ...targetItemsWithoutActive.slice(0, insertIndex),
      activeItem,
      ...targetItemsWithoutActive.slice(insertIndex),
    ];
    const movedIndex = nextItems.findIndex((item) => item.id === activeItem.id);
    if (movedIndex < 0) {
      return;
    }
    if (sourceGroup.key === targetGroup.key && sourceGroup.items.findIndex((item) => item.id === activeItem.id) === movedIndex) {
      return;
    }

    const orderInput = {
      boardId: board.id,
      boardViewId: view.id,
      groupKey: targetGroup.key,
      nextWorkObjectId: nextItems[movedIndex + 1]?.workObject.id ?? null,
      orderedWorkObjectIds: nextItems.map((item) => item.workObject.id),
      previousWorkObjectId: nextItems[movedIndex - 1]?.workObject.id ?? null,
      workObjectId: activeItem.workObject.id,
    };

    if (sourceGroup.key === targetGroup.key) {
      await props.onMoveOrder(orderInput);
      return;
    }

    await props.onMoveToGroup({
      ...orderInput,
      boardItemId: activeItem.id,
      targetGroupValue: targetGroup.value,
    });
  }

  async function createInlineWorkItem(group: BoardItemGroup) {
    const title = inlineTitle.trim();
    const typeId = props.types[0]?.id;
    if (!title || !typeId) {
      return;
    }
    const groupOptions = workItemCreateGroupOptions(group, view, board);
    setIsInlineCreating(true);
    try {
      await props.onCreateWorkInline(
        {
          title,
          typeId,
          values: [],
        },
        groupOptions,
      );
      setCreatingGroupKey(null);
      setInlineTitle("");
    } finally {
      setIsInlineCreating(false);
    }
  }

  function persistColumnOrder(nextColumnOrder: string[]) {
    const visibleFieldIdsInView = visibleColumnFields.map((field) => field.id);
    const nextVisibleFieldIds = [
      ...nextColumnOrder
        .map((columnId) => columnIdToFieldDefinitionId(columnId, visibleColumnFields))
        .filter((fieldId): fieldId is string => Boolean(fieldId)),
      ...visibleFieldIdsInView,
    ].filter((fieldId, index, all) => all.indexOf(fieldId) === index);
    const nextVisibleIds = new Set(nextVisibleFieldIds);
    const fieldStateById = new Map(view.fields.map((field) => [field.fieldDefinitionId, field]));
    const lockedFieldIds = view.fields
      .filter((field) => field.locked || fieldById.get(field.fieldDefinitionId)?.key === "title")
      .map((field) => field.fieldDefinitionId);
    const hiddenFieldIds = view.fields
      .filter((field) => !field.visible && !nextVisibleIds.has(field.fieldDefinitionId) && !lockedFieldIds.includes(field.fieldDefinitionId))
      .map((field) => field.fieldDefinitionId);
    const orderedFieldIds = [...lockedFieldIds, ...nextVisibleFieldIds, ...hiddenFieldIds].filter(
      (fieldId, index, all) => all.indexOf(fieldId) === index && fieldStateById.has(fieldId),
    );

    void props.onChangeVisibleFields({
      boardId: board.id,
      boardViewId: view.id,
      fields: orderedFieldIds.map((fieldDefinitionId) => {
        const fieldState = fieldStateById.get(fieldDefinitionId);
        return {
          fieldDefinitionId,
          summaryOperator: fieldState?.summaryOperator ?? null,
          visible: fieldState?.visible ?? nextVisibleIds.has(fieldDefinitionId),
        };
      }),
    });
  }

  function updateSummaryOperator(fieldDefinitionId: string, summaryOperator: BoardViewField["summaryOperator"]) {
    void props.onChangeVisibleFields({
      boardId: board.id,
      boardViewId: view.id,
      fields: view.fields.map((field) => ({
        fieldDefinitionId: field.fieldDefinitionId,
        summaryOperator: field.fieldDefinitionId === fieldDefinitionId ? summaryOperator : field.summaryOperator ?? null,
        visible: field.visible,
      })),
    });
  }

  return (
    <div className="w-full min-w-0">
      <RemoveBoardItemsDialog
        disabled={props.disabled}
        items={itemsToRemove}
        open={itemsToRemove.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setItemsToRemove([]);
          }
        }}
        onRemove={removeFromBoard}
      />
      <DataTable
        columns={columns}
        data={data}
        defaultColumnOrder={["name", ...visibleColumnFields.map((field) => (field.key === "type" ? "type" : `field:${field.id}`))]}
        enableColumnReordering
        enableColumnResizing
        enableColumnVisibility={false}
        enableRowReordering={canDragOrder && !props.disabled}
        enableRowSelection
        fixedColumnIds={layout.pinFirstColumn ? ["name"] : []}
        toolbar={(table) => {
          const selected = table.getSelectedRowModel().rows.map((row) => row.original);
          if (selected.length === 0) return null;
          return (
            <BulkActionsBar
              board={board}
              disabled={props.disabled}
              items={selected}
              onArchive={() => setItemsToRemove(selected)}
              onClear={() => table.resetRowSelection()}
              onUpdate={props.onUpdate}
              sprintTrackBoardId={props.sprintTrackBoardId ?? null}
            />
          );
        }}
        toolbarPlacement="floating"
        getRowId={(item) => item.id}
        groupRows={(rows): DataTableGroup<BoardItemView>[] =>
          groups.map((group) => ({
            key: group.key,
            label: group.key === "all" ? null : group.label,
            rows: rows.filter((row) => group.items.some((item) => item.id === row.original.id)),
          }))
        }
        onColumnOrderChange={persistColumnOrder}
        onRowReorder={({ activeRow, nextRow, previousRow, targetGroup }) => {
          void moveRow({
            activeItem: activeRow.original,
            nextItem: nextRow?.original ?? null,
            previousItem: previousRow?.original ?? null,
            targetGroupKey: targetGroup.key,
          });
        }}
        renderColumnFooter={(column, _table, group) => {
          if (!hasAnySummary) {
            return null;
          }
          const field =
            column.id === "type"
              ? visibleColumnFields.find((item) => item.key === "type")
              : column.id.startsWith("field:")
                ? fieldById.get(column.id.slice("field:".length))
                : undefined;
          const viewField = field ? viewFieldByFieldId.get(field.id) : undefined;
          if (!field || !viewField?.visible) {
            return null;
          }

          return (
            <ColumnSummaryFooter
              disabled={props.disabled}
              field={field}
              onChange={(operator) => updateSummaryOperator(field.id, operator)}
              summary={
                viewField.summaryOperator
                  ? viewField.summaries?.find((summary) => summary.groupKey === group.key) ??
                    calculateFieldSummaryForRows(field, viewField.summaryOperator, group.rows.map((row) => row.original), board)
                  : null
              }
              summaryOperator={viewField.summaryOperator}
            />
          );
        }}
        renderTableFooter={(column) => {
          if (!hasAnySummary) {
            return null;
          }
          if (column.id === "name") {
            return <span className="px-2 text-xs font-medium text-muted-foreground">{t("board.total")}</span>;
          }
          const field =
            column.id === "type"
              ? visibleColumnFields.find((item) => item.key === "type")
              : column.id.startsWith("field:")
                ? fieldById.get(column.id.slice("field:".length))
                : undefined;
          const viewField = field ? viewFieldByFieldId.get(field.id) : undefined;
          if (!field || !viewField?.visible) {
            return null;
          }
          return (
            <ColumnSummaryFooter
              disabled={props.disabled}
              field={field}
              onChange={(operator) => updateSummaryOperator(field.id, operator)}
              // Aggregate across the whole selection (all rows), not a single group.
              summary={
                viewField.summaryOperator
                  ? calculateFieldSummaryForRows(field, viewField.summaryOperator, data, board)
                  : null
              }
              summaryOperator={viewField.summaryOperator}
            />
          );
        }}
        renderGroupAddRow={(group, _table, { isDropTarget }) =>
          creatingGroupKey === group.key ? (
            <InlineWorkItemCreateRow
              disabled={props.disabled || isInlineCreating || !props.types[0]}
              onCancel={() => {
                setCreatingGroupKey(null);
                setInlineTitle("");
              }}
              onChange={setInlineTitle}
              onSubmit={() => {
                const targetGroup = groups.find((item) => item.key === group.key);
                if (targetGroup) {
                  return createInlineWorkItem(targetGroup);
                }
              }}
              value={inlineTitle}
            />
          ) : (
            <button
              className={cn(
                "group/add-row flex h-full w-full min-w-0 items-center gap-2 rounded-md text-left text-muted-foreground/65 transition-colors hover:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
                isDropTarget && "text-muted-foreground/80 opacity-75",
              )}
              disabled={props.disabled || !props.types[0]}
              onClick={() => {
                setCreatingGroupKey(group.key);
                setInlineTitle("");
              }}
              type="button"
            >
              <Plus className="size-4 shrink-0 text-muted-foreground/55" />
              {isDropTarget ? (
                <span className="min-w-0 truncate">{t("board.dropHere")}</span>
              ) : (
                <span className="min-w-0 truncate">{t("board.addWorkItem")}</span>
              )}
            </button>
          )
        }
        renderRowContextMenu={(row) => (
          <>
            <AddToSprintContextItems
              disabled={props.disabled}
              sprintTrackBoardId={props.sprintTrackBoardId ?? null}
              workObjectId={row.original.workObject.id}
            />
            <ContextMenuItem
              disabled={props.disabled}
              variant="destructive"
              onClick={() => {
                setItemsToRemove([row.original]);
              }}
            >
              {t("board.removeFromBoard")}
            </ContextMenuItem>
          </>
        )}
        rowSize={layout.rowSize}
        wrapText={layout.wrapText}
      />
    </div>
  );
}

function InlineWorkItemCreateRow({
  disabled,
  onCancel,
  onChange,
  onSubmit,
  value,
}: {
  disabled: boolean;
  onCancel: () => void;
  onChange: (value: string) => void;
  onSubmit: () => Promise<void> | void;
  value: string;
}) {
  const t = useT();
  const submittedRef = useRef(false);

  function submitOnce() {
    if (submittedRef.current) {
      return;
    }
    submittedRef.current = true;
    void Promise.resolve(onSubmit()).catch(() => {
      submittedRef.current = false;
    });
  }

  return (
    <div className="flex h-full w-full min-w-0 items-center gap-2 text-muted-foreground transition-colors">
      <Plus className="size-4 shrink-0 text-muted-foreground/55" />
      <input
        autoFocus
        className="h-full min-w-0 flex-1 bg-transparent text-sm font-medium text-foreground outline-none placeholder:text-muted-foreground/55"
        disabled={disabled}
        onBlur={() => {
          if (value.trim()) {
            submitOnce();
          } else {
            onCancel();
          }
        }}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            submitOnce();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
        placeholder={t("board.title")}
        value={value}
      />
    </div>
  );
}

function KanbanBoardView(props: BoardItemsProps) {
  const { locale } = useI18n();
  const t = useT();
  const { board, fields, view } = props;
  const rawConfig = normalizeViewConfig(view.config);
  const [manualSortOverride, setManualSortOverride] = useState(false);
  const effectiveView = useMemo(
    () =>
      manualSortOverride && rawConfig.sorts.length > 0
        ? { ...view, config: { ...view.config, sorts: [] } }
        : view,
    [manualSortOverride, rawConfig.sorts, view],
  );
  const groups = buildBoardItemGroups(board, effectiveView, fields, t, locale, props.users);
  const canDragItems = !props.disabled && isWritableGroupBy(rawConfig.groupBy, board, fields);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [optimisticGroups, setOptimisticGroups] = useState<BoardItemGroup[] | null>(null);
  const boardScrollRef = useRef<HTMLDivElement | null>(null);
  const rawSortsRef = useRef(rawConfig.sorts);
  const kanbanCommandRef = useRef({
    boardId: board.id,
    onChangeSort: props.onChangeSort,
    onMoveOrder: props.onMoveOrder,
    onMoveToGroup: props.onMoveToGroup,
    viewId: view.id,
  });
  useRepairManualViewOrder({
    board,
    disabled: props.disabled || activeItemId !== null,
    groups,
    onReplaceOrder: props.onReplaceOrder,
    view: effectiveView,
  });
  const renderedGroups = optimisticGroups ?? groups;

  useEffect(() => {
    rawSortsRef.current = rawConfig.sorts;
    if (rawConfig.sorts.length === 0) {
      setManualSortOverride(false);
    }
  }, [rawConfig.sorts]);

  useEffect(() => {
    kanbanCommandRef.current = {
      boardId: board.id,
      onChangeSort: props.onChangeSort,
      onMoveOrder: props.onMoveOrder,
      onMoveToGroup: props.onMoveToGroup,
      viewId: view.id,
    };
  }, [board.id, props.onChangeSort, props.onMoveOrder, props.onMoveToGroup, view.id]);

  useEffect(() => {
    if (!optimisticGroups) {
      return;
    }
    if (boardItemGroupOrderSignature(groups) === boardItemGroupOrderSignature(optimisticGroups)) {
      setOptimisticGroups(null);
    }
  }, [groups, optimisticGroups]);

  useEffect(() => {
    setOptimisticGroups(null);
  }, [board.id, view.id]);

  function handleDragStart(itemId: string) {
    setOptimisticGroups(null);
    if (rawSortsRef.current.length > 0) {
      setManualSortOverride(true);
    }
    setActiveItemId(itemId);
  }

  function handleDragEnd(result: MultiContainerDropResult | null) {
    setActiveItemId(null);
    if (!result) {
      if (rawSortsRef.current.length > 0) {
        setManualSortOverride(false);
      }
      return;
    }

    const sourceGroup = groups.find((group) => group.key === result.sourceContainerKey);
    const targetGroup = groups.find((group) => group.key === result.targetContainerKey);
    const activeItem = sourceGroup?.items.find((item) => item.id === result.itemId);
    if (!sourceGroup || !targetGroup || !activeItem) {
      if (rawSortsRef.current.length > 0) {
        setManualSortOverride(false);
      }
      return;
    }

    // The engine hands back the final per-container ordering; groups just
    // adopt it (items resolve by id across the whole board).
    const itemById = new Map(groups.flatMap((group) => group.items.map((item) => [item.id, item] as const)));
    const committedGroups = groups.map((group) => {
      const container = result.containers.find((candidate) => candidate.key === group.key);
      if (!container) {
        return group;
      }
      return {
        ...group,
        items: container.itemIds
          .map((id) => itemById.get(id))
          .filter((item): item is BoardItemView => Boolean(item)),
      };
    });
    const committedTargetGroup = committedGroups.find((group) => group.key === result.targetContainerKey);
    const movedIndex = committedTargetGroup?.items.findIndex((item) => item.id === activeItem.id) ?? -1;
    if (!committedTargetGroup || movedIndex < 0) {
      if (rawSortsRef.current.length > 0) {
        setManualSortOverride(false);
      }
      return;
    }
    setOptimisticGroups(committedGroups);

    const orderInput = {
      boardId: kanbanCommandRef.current.boardId,
      boardViewId: kanbanCommandRef.current.viewId,
      groupKey: committedTargetGroup.key,
      nextWorkObjectId: committedTargetGroup.items[movedIndex + 1]?.workObject.id ?? null,
      orderedWorkObjectIds: committedTargetGroup.items.map((item) => item.workObject.id),
      previousWorkObjectId: committedTargetGroup.items[movedIndex - 1]?.workObject.id ?? null,
      workObjectId: activeItem.workObject.id,
    };
    const persistManualSort = async () => {
      if (rawSortsRef.current.length > 0) {
        await kanbanCommandRef.current.onChangeSort({
          boardId: kanbanCommandRef.current.boardId,
          boardViewId: kanbanCommandRef.current.viewId,
          sorts: [],
        });
      }
    };

    if (sourceGroup.key === committedTargetGroup.key) {
      void persistManualSort()
        .then(() => kanbanCommandRef.current.onMoveOrder(orderInput))
        .catch(() => {
          setOptimisticGroups(null);
          if (rawSortsRef.current.length > 0) {
            setManualSortOverride(false);
          }
        });
      return;
    }

    void persistManualSort().then(() =>
      kanbanCommandRef.current.onMoveToGroup({
        ...orderInput,
        boardItemId: activeItem.id,
        targetGroupValue: committedTargetGroup.value,
      }),
    ).catch(() => {
      setOptimisticGroups(null);
      if (rawSortsRef.current.length > 0) {
        setManualSortOverride(false);
      }
    });
  }

  function handleBoardWheel(event: WheelEvent<HTMLDivElement>) {
    const scrollElement = boardScrollRef.current;
    if (!scrollElement) {
      return;
    }

    const horizontalDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
    if (horizontalDelta === 0) {
      return;
    }

    event.preventDefault();
    scrollElement.scrollLeft += horizontalDelta;
  }

  const kanbanLayout = normalizeViewLayout(rawConfig.layout);
  const groupedByStatus = rawConfig.groupBy !== null && rawConfig.groupBy === statusGroupByForBoard(board);
  const itemsById = new Map(renderedGroups.flatMap((group) => group.items.map((item) => [item.id, item] as const)));

  return (
    <MultiContainerDnd
      containers={renderedGroups.map((group) => ({ itemIds: group.items.map((item) => item.id), key: group.key }))}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      renderOverlay={(itemId) => {
        const item = itemsById.get(itemId);
        if (!item) {
          return null;
        }
        return (
          <KanbanCard
            groupedByStatus={groupedByStatus}
            item={item}
            layout={kanbanLayout}
            presentation="overlay"
            {...props}
          />
        );
      }}
    >
      {(liveContainers) => (
        <div
          className="h-full min-h-0 w-full min-w-0 overflow-x-auto overflow-y-hidden"
          onWheel={handleBoardWheel}
          ref={boardScrollRef}
        >
          <div className="flex h-full min-h-0 w-max min-w-full gap-3 pr-2">
            {liveContainers.map((container) => {
              const group = renderedGroups.find((candidate) => candidate.key === container.key);
              if (!group) {
                return null;
              }
              // Column contents follow the drag state, so cross-column
              // transfers render live while the pointer moves.
              const liveGroup = {
                ...group,
                items: container.itemIds
                  .map((id) => itemsById.get(id))
                  .filter((item): item is BoardItemView => Boolean(item)),
              };
              return (
                <KanbanColumn
                  activeItemId={activeItemId}
                  canDragOrder={canDragItems}
                  group={liveGroup}
                  groupedByStatus={groupedByStatus}
                  key={group.key}
                  layout={kanbanLayout}
                  {...props}
                />
              );
            })}
          </div>
        </div>
      )}
    </MultiContainerDnd>
  );
}

function KanbanColumnHeader({
  color,
  count,
  label,
  showColor,
}: {
  color: string | null;
  count: number;
  label: string;
  showColor: boolean;
}) {
  return (
    <div className="flex h-8 items-center gap-2 px-1">
      {showColor && color ? (
        <span
          className="inline-flex h-5.5 min-w-0 items-center gap-1.5 rounded-md px-2 text-[11px] font-semibold tracking-wide uppercase"
          style={{ backgroundColor: `${color}26`, color }}
        >
          <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
          <span className="min-w-0 truncate">{label}</span>
        </span>
      ) : (
        <h2 className="min-w-0 truncate text-xs font-semibold tracking-wide uppercase text-foreground/80">{label}</h2>
      )}
      <span className="text-xs font-medium text-muted-foreground/70 tabular-nums">{count}</span>
    </div>
  );
}

function KanbanColumn({
  activeItemId,
  canDragOrder,
  group,
  groupedByStatus,
  layout,
  ...props
}: BoardItemsProps & {
  activeItemId: string | null;
  canDragOrder: boolean;
  group: BoardItemGroup;
  groupedByStatus: boolean;
  layout: BoardViewLayout;
}) {
  const t = useT();
  const [isExpandedOverride, setIsExpandedOverride] = useState(false);
  // The live group can hold the dragged card mid-transfer; ignore it when
  // deciding whether the column reads as empty.
  const settledItems = group.items.filter((item) => item.id !== activeItemId);
  const isEmpty = settledItems.length === 0 && group.totalCount === 0;
  const isCollapsed = layout.collapseEmptyColumns && isEmpty && !isExpandedOverride && activeItemId === null;
  const { isOver, setNodeRef } = useDndContainer(group.key, { childCount: settledItems.length });
  const holdsActiveItem = activeItemId !== null && group.items.some((item) => item.id === activeItemId);

  useEffect(() => {
    if (!isEmpty) {
      setIsExpandedOverride(false);
    }
  }, [isEmpty]);

  if (isCollapsed) {
    return (
      <section
        className={cn(
          "flex h-full min-h-0 w-10 shrink-0 flex-col items-center gap-2 rounded-lg bg-muted/20 py-2 transition-colors",
          (isOver || holdsActiveItem) && "bg-muted/40",
        )}
        ref={setNodeRef}
      >
        <button
          aria-label={t("a11y.expandGroup", { group: group.label })}
          className="flex min-h-0 flex-1 cursor-pointer flex-col items-center gap-2 rounded-md px-1 py-1.5 hover:bg-muted/40"
          onClick={() => setIsExpandedOverride(true)}
          type="button"
        >
          {layout.showColumnColors && group.color ? (
            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: group.color }} />
          ) : null}
          <span
            className="min-h-0 truncate text-[11px] font-semibold tracking-wide uppercase text-muted-foreground"
            style={{ writingMode: "vertical-rl" }}
          >
            {group.label}
          </span>
          <span className="text-[11px] font-medium text-muted-foreground/60 tabular-nums">0</span>
        </button>
      </section>
    );
  }

  return (
    <section
      className={cn(
        "flex h-full min-h-0 w-[19rem] shrink-0 flex-col gap-1.5 rounded-lg bg-muted/20 p-2 transition-colors",
        (isOver || holdsActiveItem) && "bg-muted/35",
      )}
    >
      <KanbanColumnHeader color={group.color} count={group.totalCount} label={group.label} showColor={layout.showColumnColors} />
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pr-1"
        ref={setNodeRef}
      >
        <DndSortableList itemIds={group.items.map((item) => item.id)}>
          {/* Spacing lives on the container as a uniform gap (not per-card
              margins): dnd-kit measures real client rects, so displacement
              and the drop position stay exact. */}
          <div className="flex min-h-full flex-col gap-2">
            {group.items.map((item) => (
              <KanbanBoardCard
                canDragOrder={canDragOrder}
                groupedByStatus={groupedByStatus}
                item={item}
                key={`${group.key}:${item.id}`}
                layout={layout}
                {...props}
              />
            ))}
          </div>
        </DndSortableList>
      </div>
          {group.nextCursor && props.onLoadNextViewPage ? (
            <Button
              className="w-full"
              disabled={props.loadingViewPageKey === `${props.view.id}:${group.value}`}
              onClick={() => void props.onLoadNextViewPage?.({
                boardId: props.board.id,
                boardViewId: props.view.id,
                groupValue: group.value,
              })}
              size="sm"
              variant="ghost"
            >
              {props.loadingViewPageKey === `${props.view.id}:${group.value}` ? <Loader2 className="animate-spin" /> : null}
              Load more
            </Button>
          ) : null}
          {props.onCreateWorkInline ? (
            <KanbanInlineCreate
              disabled={props.disabled || !props.types[0]}
              onCreate={async (title) => {
                const typeId = props.types[0]?.id;
                if (!typeId || !props.onCreateWorkInline) {
                  return;
                }
                await props.onCreateWorkInline(
                  { title, typeId, values: [] },
                  workItemCreateGroupOptions(group, props.view, props.board),
                );
              }}
            />
          ) : null}
    </section>
  );
}

function KanbanInlineCreate({ disabled, onCreate }: { disabled: boolean; onCreate: (title: string) => Promise<void> }) {
  const t = useT();
  const [isAdding, setIsAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  async function submit() {
    const nextTitle = title.trim();
    if (!nextTitle || isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      await onCreate(nextTitle);
      setTitle("");
      setIsAdding(false);
    } finally {
      setIsSaving(false);
    }
  }

  if (!isAdding) {
    return (
      <button
        className="flex h-8 w-full shrink-0 items-center gap-1.5 rounded-md px-2 text-left text-xs font-medium text-muted-foreground/65 transition-colors hover:bg-muted/40 hover:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
        disabled={disabled}
        onClick={() => setIsAdding(true)}
        type="button"
      >
        <Plus className="size-3.5 shrink-0" />
        {t("board.addWorkItem")}
      </button>
    );
  }

  return (
    <div className="shrink-0 rounded-md bg-card p-2 shadow-xs">
      <Input
        aria-label={t("board.itemName")}
        autoFocus
        className="h-7 border-none bg-transparent px-1 text-sm shadow-none focus-visible:ring-0"
        disabled={isSaving}
        onBlur={() => {
          if (!title.trim()) {
            setIsAdding(false);
          }
        }}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            void submit();
          }
          if (event.key === "Escape") {
            setTitle("");
            setIsAdding(false);
          }
        }}
        placeholder={t("board.itemNamePlaceholder")}
        value={title}
      />
      <div className="mt-1.5 flex items-center justify-end gap-1">
        <Button
          onClick={() => {
            setTitle("");
            setIsAdding(false);
          }}
          size="sm"
          variant="ghost"
        >
          {t("common.cancel")}
        </Button>
        <Button disabled={isSaving || !title.trim()} onClick={() => void submit()} size="sm">
          {isSaving ? <Loader2 className="animate-spin" /> : null}
          Add
        </Button>
      </div>
    </div>
  );
}

function KanbanBoardCard({
  canDragOrder,
  groupedByStatus,
  item,
  layout,
  ...props
}: BoardItemsProps & {
  canDragOrder: boolean;
  groupedByStatus: boolean;
  item: BoardItemView;
  layout: BoardViewLayout;
}) {
  const sortable = useDndSortableItem(item.id, { disabled: !canDragOrder });
  return (
    <KanbanCard
      dragProps={sortable.dragProps}
      groupedByStatus={groupedByStatus}
      item={item}
      layout={layout}
      onOpenSuppressed={sortable.wasRecentlyDragged}
      presentation={sortable.isDragSource ? "source" : "default"}
      rootRef={sortable.setNodeRef}
      sortableStyle={sortable.style}
      {...props}
    />
  );
}

// A "document" view is a single full-width page backed by the view's own
// document (lazily created server-side on first open).
function DocumentBoardView({ disabled, viewId }: { disabled: boolean; viewId: string }) {
  const t = useT();
  const viewDocument = useViewDocument(viewId);
  if (!viewDocument.data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  return (
    <div className="mx-auto w-full max-w-3xl px-1 py-6">
      <DocumentEditor
        document={viewDocument.data}
        editable={!disabled}
        editorClassName="min-h-[55vh]"
        key={viewDocument.data.id}
        onReload={viewDocument.reload}
        persist={viewDocument.persist}
        placeholder={t("board.writePage")}
      />
    </div>
  );
}

// "Add to sprint" entries for item context menus, shown on any board of a
// space that has a sprint track. The sprints.list query is shared through the
// react-query cache, so many menus cost one request.
function AddToSprintContextItems({
  disabled,
  sprintTrackBoardId,
  workObjectId,
}: {
  disabled: boolean;
  sprintTrackBoardId: string | null;
  workObjectId: string;
}) {
  const t = useT();
  const sprints = useSprints(sprintTrackBoardId);
  if (!sprintTrackBoardId) return null;
  const openSprints = (sprints.data?.sprints ?? []).filter((sprint) => !sprint.closedAt);
  const currentSprintId = sprints.data?.items.find((item) => item.workObject.id === workObjectId)?.sprintId ?? null;
  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger disabled={disabled}>{t("board.addToSprint")}</ContextMenuSubTrigger>
      <ContextMenuSubContent className="w-44">
        {openSprints.map((sprint) => (
          <ContextMenuItem
            disabled={sprint.id === currentSprintId}
            key={sprint.id}
            onClick={() => void sprints.addItems({ sprintId: sprint.id, workObjectIds: [workObjectId] })}
          >
            {sprint.name}
          </ContextMenuItem>
        ))}
        <ContextMenuItem
          disabled={currentSprintId === null && sprints.data?.items.some((item) => item.workObject.id === workObjectId) === true}
          onClick={() => void sprints.addItems({ sprintId: null, workObjectIds: [workObjectId] })}
        >
          {t("sprint.backlog")}
        </ContextMenuItem>
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

// Floating actions over the list's row selection: batch status change,
// sprint planning and removal. Operations apply sequentially per item.
function BulkActionsBar({
  board,
  disabled,
  items,
  onArchive,
  onClear,
  onUpdate,
  sprintTrackBoardId,
}: {
  board: BoardView;
  disabled: boolean;
  items: BoardItemView[];
  onArchive: () => void;
  onClear: () => void;
  onUpdate: (input: { boardItemId: string; boardStatusId: string }) => Promise<unknown>;
  sprintTrackBoardId: string | null;
}) {
  const t = useT();
  const sprints = useSprints(sprintTrackBoardId);
  const [isApplying, setIsApplying] = useState(false);
  const openSprints = (sprints.data?.sprints ?? []).filter((sprint) => !sprint.closedAt);
  const busy = disabled || isApplying;

  async function applyStatus(statusId: string) {
    setIsApplying(true);
    try {
      for (const item of items) {
        if (item.status.id !== statusId) await onUpdate({ boardItemId: item.id, boardStatusId: statusId });
      }
      onClear();
    } finally {
      setIsApplying(false);
    }
  }

  async function addToSprint(sprintId: string | null) {
    setIsApplying(true);
    try {
      await sprints.addItems({ sprintId, workObjectIds: items.map((item) => item.workObject.id) });
      onClear();
    } finally {
      setIsApplying(false);
    }
  }

  return (
    <>
      <span className="px-1 text-xs tabular-nums text-muted-foreground">
        {items.length} selected
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button disabled={busy} size="sm" variant="ghost">
              {isApplying ? <Loader2 className="animate-spin" data-icon="inline-start" /> : null}
              Set status
              <ChevronDown data-icon="inline-end" className="text-muted-foreground" />
            </Button>
          }
        />
        <DropdownMenuContent align="center" className="w-44">
          {board.statuses.map((status) => (
            <DropdownMenuItem key={status.id} onClick={() => void applyStatus(status.id)}>
              <span className="size-2 rounded-full" style={{ backgroundColor: status.color }} />
              {status.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {sprintTrackBoardId ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button disabled={busy} size="sm" variant="ghost">
                {t("board.addToSprint")}
                <ChevronDown data-icon="inline-end" className="text-muted-foreground" />
              </Button>
            }
          />
          <DropdownMenuContent align="center" className="w-48">
            {openSprints.map((sprint) => (
              <DropdownMenuItem key={sprint.id} onClick={() => void addToSprint(sprint.id)}>
                {sprint.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onClick={() => void addToSprint(null)}>{t("sprint.backlog")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <Button className="text-red-500 hover:text-red-400" disabled={busy} onClick={onArchive} size="sm" variant="ghost">
        {t("board.remove")}
      </Button>
      <Button aria-label={t("board.clearSelection")} className="size-7 text-muted-foreground" onClick={onClear} size="icon-sm" variant="ghost">
        <X className="size-3.5" />
      </Button>
    </>
  );
}

// Rendered only during replay: how long the item had been sitting in its
// current status at the playhead moment (from the status-event journal).
function ReplayStatusSinceBadge({ className, workObjectId }: { className?: string; workObjectId: string }) {
  const t = useT();
  const labels = useContext(SprintReplayStatusSinceContext);
  const label = labels?.get(workObjectId);
  if (!label) return null;
  return (
    <span
      className={cn("flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground", className)}
      title={t("board.timeInStatusAt")}
    >
      <History className="size-3" />
      {label} in status
    </span>
  );
}

function KanbanCard({
  board,
  disabled,
  dragProps,
  fields,
  groupedByStatus,
  item,
  layout,
  onArchive,
  onOpenItem,
  onOpenSuppressed,
  onUpdate,
  onUpdateType,
  presentation = "default",
  rootRef,
  sortableStyle,
  sprintTrackBoardId,
  types,
  users,
  view,
}: BoardItemsProps & {
  dragProps?: Record<string, unknown>;
  groupedByStatus: boolean;
  item: BoardItemView;
  layout: BoardViewLayout;
  onOpenSuppressed?: () => boolean;
  presentation?: "default" | "overlay" | "source";
  rootRef?: Ref<HTMLElement>;
  sortableStyle?: CSSProperties;
}) {
  const t = useT();
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  // The overlay clone mounts flat and tilts on the next painted frame, so the
  // lift reads as motion instead of an instant snap into the rotated pose.
  const [isOverlayLifted, setIsOverlayLifted] = useState(false);
  useEffect(() => {
    if (presentation !== "overlay") {
      return;
    }
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => setIsOverlayLifted(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [presentation]);
  const visibleFieldIds = visibleFieldIdsForView(view);
  const fieldById = new Map(fields.map((field) => [field.id, field]));
  const visibleFieldIdSet = new Set(visibleFieldIds);
  const typeField = fields.find((field) => field.key === "type");
  const showType = (!typeField || visibleFieldIdSet.has(typeField.id)) && layout.cardSize !== "compact";
  const cardFieldIds = layout.cardSize === "compact"
    ? []
    : visibleFieldIdsForItem(visibleFieldIds, fieldById, item).filter((fieldId) => {
        const field = fieldById.get(fieldId);
        return field !== undefined && field.key !== "title" && field.key !== "type" && !isBoardStatusField(board, field);
      });
  const cardFields = cardFieldIds
    .map((fieldId) => fieldById.get(fieldId))
    .filter((field): field is WorkObjectFieldDefinition => Boolean(field))
    .filter((field) => layout.showEmptyFields || fieldValueValues(item, field.id).length > 0);
  const showStatus = !groupedByStatus;

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger
          render={
            <article
              className={cn(
                "group/card min-w-0 cursor-pointer rounded-lg bg-card shadow-xs transition-shadow hover:shadow-md",
                layout.cardSize === "compact" ? "p-2.5" : "p-3",
                dragProps && "touch-none select-none",
                // The in-list original dims while its overlay clone follows
                // the pointer; it must keep its size so siblings displace
                // around a real gap.
                presentation === "source" && "opacity-40",
                presentation === "overlay" &&
                  cn(
                    "pointer-events-none opacity-95 shadow-lg transition-transform duration-200 ease-out",
                    isOverlayLifted ? "rotate-2" : "rotate-0",
                  ),
              )}
              onClick={(event) => {
                // A click still fires on the drag source after the pointer is
                // released — don't let a finished drag open the card.
                if (!event.defaultPrevented && !onOpenSuppressed?.()) {
                  onOpenItem(item);
                }
              }}
              {...dragProps}
              ref={rootRef}
              style={sortableStyle}
            />
          }
        >
          {/* The title leads the card; the key rides with it as the stable
              reference people quote in commits and chat. */}
          <h3 className={cn("text-sm font-medium leading-snug", layout.cardSize === "compact" ? "line-clamp-2" : "line-clamp-3")}>
            {item.workObject.itemKey ? (
              <span className="mr-1.5 font-mono text-[11px] font-normal text-muted-foreground/60">{item.workObject.itemKey}</span>
            ) : null}
            {item.workObject.title}
          </h3>
          {showType ? (
            <div className="mt-1.5 flex min-w-0 items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
              <BoardTypeSelect disabled={disabled} item={item} onUpdate={onUpdateType} types={types} />
            </div>
          ) : null}
          {cardFields.length > 0 ? (
            layout.cardSize === "large" ? (
              <div className="mt-2.5 flex flex-col gap-1.5">
                {cardFields.map((field) => (
                  <div className="flex min-w-0 items-center gap-2" key={field.id}>
                    <span className="w-24 shrink-0 truncate text-[11px] text-muted-foreground/60">{field.name}</span>
                    <div className="flex min-w-0 flex-wrap items-center gap-1">
                      <KanbanFieldChip field={field} hideFieldName item={item} users={users} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 flex min-w-0 flex-wrap items-center gap-1">
                {cardFields.map((field) => (
                  <KanbanFieldChip field={field} item={item} key={field.id} users={users} />
                ))}
              </div>
            )
          ) : null}
          {showStatus ? (
            <div className="mt-2.5 flex min-w-0" onClick={(event) => event.stopPropagation()}>
              <BoardStatusSelect board={board} className="max-w-full" disabled={disabled} item={item} onOpenItem={onOpenItem} onUpdate={onUpdate} />
            </div>
          ) : null}
          <ReplayStatusSinceBadge className="mt-2" workObjectId={item.workObject.id} />
        </ContextMenuTrigger>
        <ContextMenuContent className="w-44">
          <AddToSprintContextItems
            disabled={disabled}
            sprintTrackBoardId={sprintTrackBoardId ?? null}
            workObjectId={item.workObject.id}
          />
          <ContextMenuItem disabled={disabled} variant="destructive" onClick={() => setIsRemoveOpen(true)}>
            {t("board.removeFromBoard")}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      <RemoveBoardItemsDialog
        disabled={disabled}
        items={[item]}
        open={isRemoveOpen}
        onOpenChange={setIsRemoveOpen}
        onRemove={async ([itemToRemove]) => {
          if (!itemToRemove) {
            return;
          }
          await onArchive({ boardItemId: itemToRemove.id });
          setIsRemoveOpen(false);
        }}
      />
    </>
  );
}

function KanbanFieldChip({
  field,
  hideFieldName,
  item,
  users,
}: {
  field: WorkObjectFieldDefinition;
  hideFieldName?: boolean;
  item: BoardItemView;
  users: WorkObjectListView["users"];
}) {
  const t = useT();
  const values = fieldValueValues(item, field.id);

  if (values.length === 0) {
    return (
      <span className="inline-flex h-5 min-w-0 max-w-40 items-center gap-1 rounded bg-muted/25 px-1.5 text-[11px] text-muted-foreground/45" title={t("board.cardFieldEmpty", { field: field.name })}>
        {hideFieldName ? null : <span className="min-w-0 truncate">{field.name}:</span>}
        <span>—</span>
      </span>
    );
  }

  if (field.type === "select" || field.type === "multi_select") {
    return (
      <>
        {values.map((value) => {
          const option = field.options.find((candidate) => candidate.id === value);
          const color = option?.color ?? "#8b5cf6";
          return (
            <span
              className="inline-flex h-5 min-w-0 max-w-40 items-center gap-1 rounded px-1.5 text-[11px] font-medium"
              key={value}
              style={{ backgroundColor: `${color}26`, color }}
              title={`${field.name}: ${option?.value ?? value}`}
            >
              {!hideFieldName ? <span className="max-w-20 shrink-0 truncate opacity-70">{field.name}:</span> : null}
              <span className="min-w-0 truncate">{option?.value ?? value}</span>
            </span>
          );
        })}
      </>
    );
  }

  if (field.type === "user_relation" || field.type === "user_multi_relation") {
    const userById = new Map(users.map((user) => [user.id, user]));
    return (
      <span className="inline-flex min-w-0 max-w-40 items-center gap-1.5" title={`${field.name}: ${values.map((value) => cardUserLabel(item, userById.get(value), value)).join(", ")}`}>
        {!hideFieldName ? <span className="max-w-20 truncate text-[11px] text-muted-foreground/70">{field.name}:</span> : null}
        <span className="inline-flex items-center -space-x-1.5">
          {values.slice(0, 4).map((value) => {
            const label = cardUserLabel(item, userById.get(value), value);
            return (
              <span
                className="grid size-5 shrink-0 place-items-center rounded-full bg-primary/20 text-[9px] font-semibold text-primary ring-2 ring-card"
                key={value}
              >
                {label.slice(0, 1).toUpperCase()}
              </span>
            );
          })}
          {values.length > 4 ? (
            <span className="grid size-5 shrink-0 place-items-center rounded-full bg-muted text-[9px] font-semibold text-muted-foreground ring-2 ring-card">
              +{values.length - 4}
            </span>
          ) : null}
        </span>
      </span>
    );
  }

  if (field.type === "date" || field.type === "date_range") {
    return (
      <span className="inline-flex h-5 min-w-0 max-w-40 items-center gap-1 rounded bg-muted/40 px-1.5 text-[11px] text-muted-foreground" title={`${field.name}: ${values.map(formatCardDate).join(" – ")}`}>
        {!hideFieldName ? <span className="max-w-20 truncate">{field.name}:</span> : null}
        <CalendarDays className="size-3 shrink-0" />
        <span className="min-w-0 truncate">{values.map(formatCardDate).join(" – ")}</span>
      </span>
    );
  }

  if (field.type === "checkbox") {
    if (values[0] !== "true") {
      return null;
    }
    return (
      <span className="inline-flex h-5 min-w-0 items-center gap-1 rounded bg-muted/40 px-1.5 text-[11px] text-muted-foreground">
        <Check className="size-3 shrink-0" />
        {hideFieldName ? null : <span className="min-w-0 truncate">{field.name}</span>}
      </span>
    );
  }

  const progress = readProgressConfig(field);
  if (progress) {
    return (
      <span className="inline-flex h-5 min-w-24 max-w-40 items-center gap-1.5 rounded bg-muted/40 px-1.5 text-[11px] text-muted-foreground" title={`${field.name}: ${fieldValueLabel(item, field)}`}>
        {!hideFieldName ? <span className="max-w-20 shrink-0 truncate">{field.name}:</span> : null}
        <FieldProgressTrack className="min-w-8 flex-1" max={progress.max} value={values[0] ?? ""} />
      </span>
    );
  }

  return (
    <span className="inline-flex h-5 min-w-0 max-w-40 items-center gap-1 rounded bg-muted/40 px-1.5 text-[11px] text-muted-foreground" title={`${field.name}: ${fieldValueLabel(item, field)}`}>
      {!hideFieldName ? <span className="max-w-20 shrink-0 truncate">{field.name}:</span> : null}
      <span className="min-w-0 truncate">{fieldValueLabel(item, field)}</span>
    </span>
  );
}

// The member list covers current members; anyone no longer in it — a removed
// member still assigned to old work — resolves from the labels the server
// shipped with the item, never from the raw id.
function cardUserLabel(item: BoardItemView, user: WorkObjectListView["users"][number] | undefined, id: string) {
  return user?.displayName ?? user?.email ?? referenceLabel(item, id);
}

function formatCardDate(value: string) {
  const date = new Date(value.includes("T") ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
}

function TimelineBoardView(props: BoardItemsProps) {
  const t = useT();
  const layout = normalizeViewLayout(props.view.config.layout);
  const queryKey = JSON.stringify({
    fields: props.fields.map((field) => `${field.id}:${field.type}`).sort(),
    filterExpression: props.view.config.filterExpression ?? null,
    sorts: props.view.config.sorts ?? [],
  });
  const { board, disabled, fields, onCreateWorkInline, onUpdate, types, users, view } = props;
  const itemSummary = useMemo(() => {
    const fieldById = new Map(fields.map((field) => [field.id, field]));
    const summaryFields = visibleFieldIdsForView(view)
      .map((fieldId) => fieldById.get(fieldId))
      .filter((field): field is WorkObjectFieldDefinition => Boolean(field));
    if (summaryFields.length === 0) return undefined;
    return (item: BoardItemView) => timelineItemFieldSummary(item, summaryFields, users, board.statusFieldDefinitionId);
  }, [board.statusFieldDefinitionId, fields, users, view]);

  const includeClosedItems = view.config.includeClosedItems ?? true;
  const grouping = useMemo<TimelineGrouping | undefined>(() => {
    const groupBy = normalizeViewConfig(view.config).groupBy;
    if (!groupBy?.startsWith("field:")) return undefined;
    const fieldId = groupBy.slice("field:".length);
    if (fieldId === board.statusFieldDefinitionId) {
      const statuses = board.statuses.filter((status) => includeClosedItems || status.category !== "closed");
      return {
        definitions: statuses.map((status) => ({ color: status.color, key: `status:${status.id}`, label: status.name, value: status.id })),
        fieldId,
        isStatus: true,
        writable: !disabled,
      };
    }
    const field = fields.find((candidate) => candidate.id === fieldId);
    if (!field) return undefined;
    if (field.type === "select" || field.type === "multi_select") {
      return {
        definitions: [
          ...field.options.map((option) => ({ color: option.color ?? null, key: `field:${fieldId}:${option.id}`, label: option.value, value: option.id })),
          { color: null, key: `field:${fieldId}:__empty__`, label: t("board.noValue"), value: "" },
        ],
        fieldId,
        isStatus: false,
        writable: !disabled && field.type === "select",
      };
    }
    // Formula/rollup groupings are read-only: sections come from the values
    // found in the loaded items.
    return { definitions: [], fieldId, isStatus: false, writable: false };
  }, [board.statusFieldDefinitionId, board.statuses, disabled, fields, includeClosedItems, view.config]);

  // Items drawn directly on the timeline take the board's first type, same as
  // inline creation in the list view.
  const createTypeId = types[0]?.id;
  const handleCreateItem = useMemo(() => {
    if (!onCreateWorkInline || !createTypeId || disabled) return undefined;
    return async (input: { boardStatusId?: string; title: string; values: Array<{ fieldDefinitionId: string; value: string | string[] }> }) => {
      await onCreateWorkInline(
        { title: input.title, typeId: createTypeId, values: input.values },
        input.boardStatusId ? { boardStatusId: input.boardStatusId } : undefined,
      );
    };
  }, [createTypeId, disabled, onCreateWorkInline]);

  const handleChangeItemStatus = useCallback(async (item: BoardItemView, boardStatusId: string) => {
    await onUpdate({ boardItemId: item.id, boardStatusId });
  }, [onUpdate]);

  return (
    <BoardTimelineView
      boardId={props.board.id}
      boardViewId={props.view.id}
      fields={props.fields}
      grouping={grouping}
      includeClosedItems={includeClosedItems}
      itemSummary={itemSummary}
      onChangeItemStatus={handleChangeItemStatus}
      onCreateItem={handleCreateItem}
      onOpenItem={props.onOpenItem}
      onUpdateFieldValues={props.onUpdateFieldValues}
      queryKey={queryKey}
      sprintScope={props.sprintScope ?? null}
      timelineDateFieldId={layout.timelineDateFieldId}
    />
  );
}

// Compact one-line summary of the view's visible fields for a timeline bar —
// small labeled parts (with color dots for statuses and select options) that
// clip to the free space between the title and the duration label without
// pushing either.
function timelineItemFieldSummary(
  item: BoardItemView,
  summaryFields: WorkObjectFieldDefinition[],
  users: WorkObjectListView["users"],
  statusFieldDefinitionId: string | null,
): TimelineRowMetaPart[] | null {
  const parts = summaryFields
    .filter((field) => fieldAppliesToType(field, item.workObject.type?.id))
    .flatMap((field): TimelineRowMetaPart[] => {
      // Status and type live on the item itself, not in field values.
      if (field.id === statusFieldDefinitionId) return [{ color: item.status.color, label: item.status.name }];
      if (field.key === "type") return item.workObject.type ? [{ label: item.workObject.type.name }] : [];
      const values = fieldValueLabels(item, field.id);
      if (values.length === 0) return [];
      if (field.type === "select" || field.type === "multi_select") {
        return values.map((value) => {
          const option = field.options.find((candidate) => candidate.id === value);
          return { color: option?.color ?? null, label: option?.value ?? value };
        });
      }
      if (field.type === "user_relation" || field.type === "user_multi_relation") {
        const userById = new Map(users.map((user) => [user.id, user]));
        return [{ label: values.map((value) => cardUserLabel(item, userById.get(value), value)).join(", ") }];
      }
      if (field.type === "date" || field.type === "date_range") {
        return [{ label: values.map(formatCardDate).join(" – ") }];
      }
      if (field.type === "checkbox") {
        return values[0] === "true" ? [{ label: `✓ ${field.name}` }] : [];
      }
      if (field.type === "work_object_relation" || field.type === "work_object_multi_relation") {
        return [{ label: values.map((value) => referenceLabel(item, value)).join(", ") }];
      }
      if (field.type === "percent") {
        return [{ label: `${values.join(", ")}%` }];
      }
      const label = fieldValueLabel(item, field);
      return label === emptyValueLabel ? [] : [{ label }];
    });
  return parts.length > 0 ? parts : null;
}

type BoardItemsProps = {
  board: BoardView;
  disabled: boolean;
  fields: WorkObjectFieldDefinition[];
  loadingViewPageKey?: string | null;
  sprintScope?: string | null;
  // The space's sprint board, when one exists — unlocks "Add to sprint"
  // actions on any board of that space.
  sprintTrackBoardId?: string | null;
  onArchive: (input: { boardItemId: string }) => Promise<void>;
  onCreateWorkInline?: (
    input: CreateWorkObjectInput,
    options?: {
      boardStatusId?: string;
      groupKey?: string;
      orderedWorkObjectIds?: string[];
      targetGroupValue?: string;
      viewId?: string;
    },
  ) => Promise<void>;
  users: WorkObjectListView["users"];
  onMoveOrder: (input: {
    boardId: string;
    boardViewId: string;
    groupKey: string;
    nextWorkObjectId?: string | null;
    orderedWorkObjectIds: string[];
    previousWorkObjectId?: string | null;
    workObjectId: string;
  }) => Promise<void>;
  onMoveToGroup: (input: {
    boardId: string;
    boardItemId: string;
    boardViewId: string;
    groupKey: string;
    nextWorkObjectId?: string | null;
    orderedWorkObjectIds: string[];
    previousWorkObjectId?: string | null;
    targetGroupValue: string;
    workObjectId: string;
  }) => Promise<void>;
  onLoadNextViewPage?: (input: { boardId: string; boardViewId: string; groupValue?: string }) => Promise<void>;
  onOpenItem: (item: BoardItemView) => void;
  onReplaceOrder: (input: { boardId: string; boardViewId: string; groupKey: string; workObjectIds: string[] }) => Promise<void>;
  onChangeSort: (input: { boardId: string; boardViewId: string; sorts: BoardViewConfig["sorts"] }) => Promise<void>;
  onChangeVisibleFields: (input: { boardId: string; boardViewId: string; fields: Array<{ fieldDefinitionId: string; summaryOperator?: BoardViewField["summaryOperator"]; visible: boolean }> }) => Promise<void>;
  onUpdate: (input: { boardItemId: string; boardStatusId: string }) => Promise<void>;
  onUpdateFieldValues: (input: UpdateWorkObjectFieldValuesInput) => Promise<void>;
  onUpdateFormulaField: (field: WorkObjectFieldDefinition, expression: string) => Promise<void>;
  onUpdateType: (input: { type: NonNullable<BoardItemView["workObject"]["type"]>; workObjectId: string }) => Promise<void>;
  types: WorkObjectListView["types"];
  view: BoardView["views"][number];
};

function RemoveBoardItemsDialog({
  disabled,
  items,
  onOpenChange,
  onRemove,
  open,
}: {
  disabled: boolean;
  items: BoardItemView[];
  onOpenChange: (open: boolean) => void;
  onRemove: (items: BoardItemView[]) => Promise<void>;
  open: boolean;
}) {
  const t = useT();
  const includesLastBoardAppearance = items.some(isLastBoardAppearance);
  const title = t("board.removeFromBoardTitle", { count: items.length });
  const description = includesLastBoardAppearance
    ? t("board.removeFromBoardArchives")
    : t("board.removeFromBoardKeeps");

  async function confirmRemove() {
    await onRemove(items);
    onOpenChange(false);
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={disabled}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            disabled={disabled || items.length === 0}
            onClick={() => {
              void confirmRemove();
            }}
            variant="destructive"
          >
            {t("board.removeFromBoard")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function isLastBoardAppearance(item: BoardItemView) {
  return item.workObject.activeBoardCount <= 1;
}

function BoardFieldValueCell({
  disabled,
  field,
  fields,
  item,
  onOpenItem,
  onUpdate,
  onUpdateFormulaField,
}: {
  disabled: boolean;
  field: WorkObjectFieldDefinition;
  fields: WorkObjectFieldDefinition[];
  item: BoardItemView;
  onOpenItem?: (item: BoardItemView) => void;
  onUpdate: (input: UpdateWorkObjectFieldValuesInput) => Promise<void>;
  onUpdateFormulaField: (field: WorkObjectFieldDefinition, expression: string) => Promise<void>;
}) {
  const applies = fieldAppliesToType(field, item.workObject.type?.id);

  if (!applies) {
    return <span className="block min-w-0 truncate text-sm text-muted-foreground/40">-</span>;
  }

  if (field.type === "formula") {
    return <BoardFormulaFieldValueCell disabled={disabled} field={field} fields={fields} item={item} onUpdateFormulaField={onUpdateFormulaField} />;
  }

  // Relations need the full list view (users / work objects) which board cells don't carry;
  // keep them read-only here and edit them in the task detail panel.
  if (isRelationField(field.type)) {
    return <span className="block min-w-0 truncate text-sm text-muted-foreground/80">{fieldValueLabel(item, field) || emptyValueLabel}</span>;
  }

  const multiple = isMultiValueField(field.type);
  const value = multiple ? fieldValueValues(item, field.id) : fieldValueDraft(item, field.id);

  return (
    <WorkObjectFieldValueEditor
      disabled={disabled}
      field={field}
      onCommit={(next) => void onUpdate({ values: [{ fieldDefinitionId: field.id, value: next }], workObjectId: item.workObject.id })}
      referenceLabels={item.relationLabels}
      surface="cell"
      value={value}
    />
  );
}

function BoardFormulaFieldValueCell({
  disabled,
  field,
  fields,
  item,
  onUpdateFormulaField,
}: {
  disabled: boolean;
  field: WorkObjectFieldDefinition;
  fields: WorkObjectFieldDefinition[];
  item: BoardItemView;
  onUpdateFormulaField: (field: WorkObjectFieldDefinition, expression: string) => Promise<void>;
}) {
  const t = useT();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(readFormulaExpression(field.config));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDraftDirty, setIsDraftDirty] = useState(false);
  const valueLabel = fieldValueLabel(item, field);
  const isRecomputing = useContext(RecomputingFieldsContext).has(field.id);

  useEffect(() => {
    if (!isOpen) {
      setDraft(readFormulaExpression(field.config));
      setSaveError(null);
      setIsDraftDirty(false);
    }
  }, [field.config, isOpen]);

  async function saveFormula() {
    setIsSaving(true);
    setSaveError(null);
    try {
      await onUpdateFormulaField(field, draft.trim());
      setIsOpen(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Could not save formula.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        className="flex h-6 w-full min-w-0 cursor-text items-center gap-1.5 rounded-md px-1.5 text-left text-sm text-muted-foreground/80 transition-colors hover:bg-muted/50 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        disabled={disabled}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {isRecomputing ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <Sigma className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 truncate">
          {isRecomputing ? "Recomputing…" : valueLabel === emptyValueLabel ? "Formula" : valueLabel}
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(38rem,calc(100vw-2rem))] gap-3 p-3" side="bottom">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-medium">{field.name}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("board.formulaGlobal")}</p>
          </div>
        </div>
        <FormulaExpressionEditor
          currentFieldId={field.id}
          disabled={isSaving}
          externalError={saveError}
          fields={fields}
          onChange={(nextExpression) => {
            setSaveError(null);
            setIsDraftDirty(true);
            setDraft(nextExpression);
          }}
          onPreview={(expression) =>
            queryClient.fetchQuery(trpc.workObjectFields.previewFormula.queryOptions({
              currentFieldId: field.id,
              expression,
              workObjectId: item.workObject.id,
            }))
          }
          scopeFieldIds={fields.map((item) => item.id)}
          showValidationMessages={isDraftDirty}
          value={draft}
        />
        <div className="flex justify-end gap-1.5">
          <Button disabled={isSaving} onClick={() => setIsOpen(false)} size="sm" type="button" variant="ghost">
            {t("common.cancel")}
          </Button>
          <Button disabled={isSaving} onClick={() => void saveFormula()} size="sm" type="button">
            {t("board.saveFormula")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RatingFieldValueCell({
  currentValue,
  disabled,
  field,
  item,
  onSave,
}: {
  currentValue: string;
  disabled: boolean;
  field: WorkObjectFieldDefinition;
  item: BoardItemView;
  onSave: (value: string) => Promise<void>;
}) {
  const t = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [draft, setDraft] = useState(currentValue);
  const [previewValue, setPreviewValue] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const scale = readRatingScale(field.config);
  const value = clampRating(Number(currentValue), scale);
  const fullStars = scale <= 10 && width >= Math.max(128, scale * 18 + 16);
  const previewRating = previewValue ?? (isOpen ? clampRating(Number(draft), scale) : value);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry?.contentRect.width ?? 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setDraft(currentValue);
    }
  }, [currentValue, isOpen]);

  async function saveRating(nextValue: number) {
    await onSave(nextValue > 0 ? String(nextValue) : "");
    setIsOpen(false);
    setPreviewValue(null);
  }

  return (
    <div className="flex h-6 min-w-0 items-center" ref={containerRef}>
      {fullStars ? (
        <div className="flex min-w-0 items-center gap-0.5" onPointerDown={(event) => event.stopPropagation()}>
          {Array.from({ length: scale }, (_, index) => {
            const nextValue = index + 1;
            const isActive = nextValue <= value;
            return (
              <motion.button
                aria-label={t("a11y.setFieldValue", { field: field.name, item: item.workObject.title, scale, value: nextValue })}
                className="grid size-4 place-items-center rounded-sm text-muted-foreground transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={disabled}
                key={nextValue}
                onClick={() => void saveRating(nextValue)}
                tabIndex={0}
                type="button"
                whileHover={{ scale: disabled ? 1 : 1.18 }}
                whileTap={{ scale: disabled ? 1 : 0.9 }}
              >
                <Star className={cn("size-3.5", isActive && "fill-current text-primary")} />
              </motion.button>
            );
          })}
        </div>
      ) : (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger
            className="inline-flex h-6 max-w-full min-w-0 items-center gap-1 rounded-md px-1.5 text-sm text-muted-foreground/80 transition-colors hover:bg-muted/50 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disabled}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <motion.span whileHover={{ scale: disabled ? 1 : 1.12 }} whileTap={{ scale: disabled ? 1 : 0.92 }}>
              <Star className={cn("size-3.5", value > 0 && "fill-current text-primary")} />
            </motion.span>
            <span className="min-w-0 truncate">{value > 0 ? `${value}/${scale}` : emptyValueLabel}</span>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto min-w-0 p-1.5" side="bottom">
            {scale <= 10 ? (
              <div
                className="flex items-center gap-1"
                onMouseLeave={() => {
                  setPreviewValue(null);
                }}
              >
                {Array.from({ length: scale }, (_, index) => {
                  const nextValue = index + 1;
                  return (
                    <motion.button
                      aria-label={t("a11y.setFieldValue", { field: field.name, item: item.workObject.title, scale, value: nextValue })}
                      className="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted/50"
                      disabled={disabled}
                      key={nextValue}
                      onClick={() => void saveRating(nextValue)}
                      onMouseEnter={() => setPreviewValue(nextValue)}
                      type="button"
                      whileHover={{ scale: 1.16 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Star className={cn("size-5 transition-colors", nextValue <= previewRating && "fill-current text-primary")} />
                    </motion.button>
                  );
                })}
                {value > 0 ? (
                  <button
                    aria-label={t("a11y.clearFieldFor", { field: field.name, item: item.workObject.title })}
                    className="ml-1 h-7 rounded-md px-2 text-xs text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={disabled}
                    onClick={() => void saveRating(0)}
                    type="button"
                  >
                    {t("board.clear")}
                  </button>
                ) : null}
              </div>
            ) : (
              <Input
                autoFocus
                className="h-8 w-24"
                disabled={disabled}
                inputMode="decimal"
                max={scale}
                min={0}
                onBlur={() => void saveRating(clampRating(Number(draft), scale))}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    void saveRating(clampRating(Number(draft), scale));
                  }
                  if (event.key === "Escape") {
                    setIsOpen(false);
                  }
                }}
                placeholder={`0-${scale}`}
                type="number"
                value={draft}
              />
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function DurationFieldValueCell({
  currentValue,
  disabled,
  field,
  item,
  onSave,
}: {
  currentValue: string;
  disabled: boolean;
  field: WorkObjectFieldDefinition;
  item: BoardItemView;
  onSave: (value: string) => Promise<void>;
}) {
  const t = useT();
  const [draft, setDraft] = useState(formatDurationFromSeconds(currentValue));
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(false);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (!isEditing) {
      setDraft(formatDurationFromSeconds(currentValue));
      setError(false);
    }
  }, [currentValue, isEditing]);

  async function saveDuration() {
    const parsed = parseDurationToSeconds(draft);
    if (parsed === null) {
      setError(true);
      return;
    }
    setError(false);
    await onSave(parsed);
    setIsEditing(false);
  }

  if (isEditing) {
    return (
      <Input
        aria-label={t("a11y.fieldFor", { field: field.name, item: item.workObject.title })}
        aria-invalid={error}
        autoFocus
        className="h-6 min-w-0 rounded-md border-border/70 bg-transparent px-1.5 py-0 text-sm text-foreground"
        disabled={disabled}
        onBlur={() => {
          if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false;
            return;
          }
          void saveDuration();
        }}
        onChange={(event) => {
          setDraft(event.target.value);
          setError(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.currentTarget.blur();
          }
          if (event.key === "Escape") {
            skipBlurSaveRef.current = true;
            setDraft(formatDurationFromSeconds(currentValue));
            setIsEditing(false);
            setError(false);
          }
        }}
        onPointerDown={(event) => event.stopPropagation()}
        placeholder="1h 30m"
        value={draft}
      />
    );
  }

  return (
    <button
      className="block h-6 w-full min-w-0 cursor-text truncate rounded-md px-1.5 text-left text-sm text-muted-foreground/80 transition-colors hover:bg-muted/50 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
      disabled={disabled}
      onClick={() => setIsEditing(true)}
      onPointerDown={(event) => event.stopPropagation()}
      type="button"
    >
      {formatDurationFromSeconds(currentValue) || emptyValueLabel}
    </button>
  );
}

function BoardTypeSelect({
  className,
  disabled,
  item,
  onUpdate,
  types,
}: {
  className?: string;
  disabled: boolean;
  item: BoardItemView;
  onUpdate: (input: { type: NonNullable<BoardItemView["workObject"]["type"]>; workObjectId: string }) => Promise<void>;
  types: WorkObjectListView["types"];
}) {
  const t = useT();
  const selectedType = types.find((type) => type.id === item.workObject.type?.id) ?? item.workObject.type ?? types[0] ?? null;

  return (
    <Select
      disabled={disabled || types.length === 0}
      onValueChange={(typeId) => {
        const nextType = types.find((type) => type.id === typeId);
        if (!nextType || nextType.id === item.workObject.type?.id) {
          return;
        }
        void onUpdate({ type: nextType, workObjectId: item.workObject.id });
      }}
      value={selectedType?.id ?? ""}
    >
      <SelectTrigger
        aria-label={t("a11y.typeFor", { item: item.workObject.title })}
        className={cn(
          "h-5 max-w-full justify-between rounded-md border-transparent bg-muted/35 px-2 py-0 text-[0.625rem] leading-none font-medium text-foreground/85 hover:bg-muted/50",
          className,
        )}
        onPointerDown={(event) => event.stopPropagation()}
        size="sm"
      >
        <span className="min-w-0 truncate text-left" data-slot="select-value">
          {selectedType?.name ?? "No type"}
        </span>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {types.map((type) => (
            <SelectItem key={type.id} value={type.id}>
              {type.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function BoardStatusSelect({
  board,
  className,
  disabled,
  item,
  onOpenItem,
  onUpdate,
}: {
  board: BoardView;
  className?: string;
  disabled: boolean;
  item: BoardItemView;
  onOpenItem?: (item: BoardItemView) => void;
  onUpdate: (input: { boardItemId: string; boardStatusId: string }) => Promise<void>;
}) {
  const t = useT();
  const selectedStatus = board.statuses.find((status) => status.id === item.status.id) ?? item.status;
  const [blockedAssessment, setBlockedAssessment] = useState<BoardTransitionAssessment | null>(null);
  const [blockedStatusName, setBlockedStatusName] = useState("");

  return (
    <>
      <Select
        disabled={disabled}
        onValueChange={(boardStatusId) => {
          if (boardStatusId === null || boardStatusId === item.status.id) return;
          setBlockedStatusName(board.statuses.find((status) => status.id === boardStatusId)?.name ?? "status");
          void onUpdate({ boardItemId: item.id, boardStatusId }).catch((error: unknown) => {
            if (error instanceof BoardTransitionBlockedError) setBlockedAssessment(error.assessment);
          });
        }}
        value={item.status.id}
      >
        <SelectTrigger
          aria-label={t("a11y.statusFor", { item: item.workObject.title })}
          className={cn("h-6 max-w-full justify-between gap-1.5 border-transparent bg-muted/25 px-2 py-0 text-xs leading-none text-muted-foreground hover:bg-muted/40", className)}
          onPointerDown={(event) => event.stopPropagation()}
          size="sm"
        >
          <span className="flex min-w-0 items-center gap-1.5 text-left" data-slot="select-value">
            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: selectedStatus.color }} />
            <span className="min-w-0 truncate">{selectedStatus.name}</span>
          </span>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {board.statuses.map((status) => (
              <SelectItem key={status.id} value={status.id}>
                <span className="flex min-w-0 items-center gap-2">
                  <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: status.color }} />
                  <span className="min-w-0 truncate">{status.name}{status.entryRule ? " · requirements" : ""}</span>
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <AlertDialog onOpenChange={(open) => !open && setBlockedAssessment(null)} open={blockedAssessment !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Can’t move to “{blockedStatusName}”</AlertDialogTitle>
            <AlertDialogDescription>{t("board.transitionBlocked")}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2" role="status">
            {blockedAssessment?.failedExpressions.map((failedExpression) => (
              <div className="flex items-start gap-2 rounded-md bg-muted/30 p-2 text-xs" key={failedExpression}>
                <X className="mt-0.5 text-destructive" />
                <code className="min-w-0 whitespace-pre-wrap break-words">{failedExpression}</code>
              </div>
            ))}
          </div>
          <AlertDialogFooter><AlertDialogAction onClick={() => { setBlockedAssessment(null); onOpenItem?.(item); }}>{t("board.openAndFix")}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

type BoardItemGroup = {
  color: string | null;
  items: BoardItemView[];
  key: string;
  label: string;
  nextCursor: string | null;
  totalCount: number;
  value: string;
};

function buildBoardItemGroups(
  board: BoardView,
  view: BoardView["views"][number] | null,
  fields: WorkObjectFieldDefinition[],
  t: Translate,
  locale: string,
  users: WorkObjectListView["users"] = [],
): BoardItemGroup[] {
  const config = view ? normalizeViewConfig(view.config) : null;
  const matchingIds = view ? new Set(view.query.workObjectIds) : null;
  const visibleItems = matchingIds ? board.items.filter((item) => matchingIds.has(item.workObject.id)) : board.items;
  if (!view || !config?.groupBy) {
    return [{
      color: null,
      items: sortItemsForGroup(visibleItems, "all", view),
      key: "all",
      label: t("board.allWork"),
      nextCursor: view?.query.nextCursor ?? null,
      totalCount: visibleItems.length,
      value: "",
    }];
  }

  const fieldId = config.groupBy.slice("field:".length);
  const bucket = config.groupBucket ?? null;
  const serverGroupsByValue = new Map(view.query.groups.map((group) => [group.value, group]));
  const loadedItemsForGroup = (value: string) => {
    const workObjectIds = new Set(serverGroupsByValue.get(value)?.workObjectIds ?? []);
    return visibleItems.filter((item) => workObjectIds.has(item.workObject.id));
  };
  if (fieldId === board.statusFieldDefinitionId) {
    const statuses = board.statuses.filter((status) => config.includeClosedItems !== false || status.category !== "closed");
    return statuses.map((status) => {
      const key = `status:${status.id}`;
      const serverGroup = serverGroupsByValue.get(status.id);
      const items = loadedItemsForGroup(status.id);
      return {
        color: status.color,
        items: sortItemsForGroup(items, key, view),
        key,
        label: status.name,
        nextCursor: serverGroup?.nextCursor ?? null,
        totalCount: serverGroup?.totalCount ?? 0,
        value: status.id,
      };
    });
  }

  const field = fields.find((item) => item.id === fieldId);
  const groups = new Map<string, BoardItemGroup>();
  groups.set(`field:${fieldId}:__empty__`, {
    color: null,
    items: [],
    key: `field:${fieldId}:__empty__`,
    label: t("board.groupNoValue", { field: field?.name ?? t("board.groupFieldFallback") }),
    nextCursor: serverGroupsByValue.get("")?.nextCursor ?? null,
    totalCount: serverGroupsByValue.get("")?.totalCount ?? 0,
    value: "",
  });

  if (field?.type === "select" || field?.type === "multi_select") {
    for (const option of field.options) {
      groups.set(`field:${fieldId}:${option.id}`, {
        color: option.color ?? null,
        items: [],
        key: `field:${fieldId}:${option.id}`,
        label: option.value,
        nextCursor: serverGroupsByValue.get(option.id)?.nextCursor ?? null,
        totalCount: serverGroupsByValue.get(option.id)?.totalCount ?? 0,
        value: option.id,
      });
    }
  }

  // Grouping by a relation field keys on ids; the labels the items carry turn
  // those keys back into names for the column headers.
  const groupReferenceLabels = Object.assign(
    {},
    // Группировка по человеку ключуется его id — подпись колонки берётся из
    // списка участников, иначе заголовок был бы «не найдено».
    Object.fromEntries(users.map((user) => [user.id, user.displayName ?? user.email])),
    ...visibleItems.map((item) => item.relationLabels ?? {}),
  ) as Record<string, string>;
  for (const serverGroup of view.query.groups) {
    const value = serverGroup.value;
      const key = value === "" ? `field:${fieldId}:__empty__` : `field:${fieldId}:${value}`;
      const label = bucket
        ? formatGroupBucketLabel(bucket, value, locale, t)
        : fieldOptionLabel(field, value, groupReferenceLabels);
      const group = groups.get(key) ?? {
        color: null,
        items: [],
        key,
        label: value === "" ? t("board.groupNoValue", { field: field?.name ?? t("board.groupFieldFallback") }) : label,
        nextCursor: serverGroup.nextCursor,
        totalCount: serverGroup.totalCount,
        value,
      };
      group.items = loadedItemsForGroup(value);
      group.nextCursor = serverGroup.nextCursor;
      group.totalCount = serverGroup.totalCount;
      groups.set(key, group);
  }

  return [...groups.values()]
    .sort((a, b) => (bucket ? compareGroupBucketValues(bucket, a.value, b.value) : a.label.localeCompare(b.label)))
    .map((group) => ({ ...group, items: sortItemsForGroup(group.items, group.key, view) }));
}

function workItemCreateGroupOptions(group: BoardItemGroup, view: BoardView["views"][number], board: BoardView) {
  const config = normalizeViewConfig(view.config);
  const orderedWorkObjectIds = group.items.map((item) => item.workObject.id);
  if (config.groupBy === statusGroupByForBoard(board) && group.value) {
    return {
      boardStatusId: group.value,
      groupKey: group.key,
      orderedWorkObjectIds,
      targetGroupValue: group.value,
      viewId: view.id,
    };
  }
  if (config.groupBy?.startsWith("field:")) {
    return {
      groupKey: group.key,
      orderedWorkObjectIds,
      targetGroupValue: group.value,
      viewId: view.id,
    };
  }
  return {
    groupKey: group.key,
    orderedWorkObjectIds,
    viewId: view.id,
  };
}

function sortItemsForGroup(items: BoardItemView[], groupKey: string, view: BoardView["views"][number] | null) {
  if (!view) {
    return [...items].sort((a, b) => a.workObject.title.localeCompare(b.workObject.title) || a.id.localeCompare(b.id));
  }
  const config = normalizeViewConfig(view.config);
  if (config.sorts.length > 0) {
    const queryRankByWorkObjectId = new Map(view.query.workObjectIds.map((workObjectId, index) => [workObjectId, index]));
    return [...items].sort((a, b) => {
      const aRank = queryRankByWorkObjectId.get(a.workObject.id);
      const bRank = queryRankByWorkObjectId.get(b.workObject.id);
      if (aRank !== undefined && bRank !== undefined) return aRank - bRank;
      if (aRank !== undefined) return -1;
      if (bRank !== undefined) return 1;
      return a.workObject.title.localeCompare(b.workObject.title) || a.id.localeCompare(b.id);
    });
  }

  const rankByWorkObjectId = new Map(
    view.orders.filter((order) => order.groupKey === groupKey).map((order) => [order.workObjectId, order.rank]),
  );
  return [...items].sort((a, b) => {
    const aRank = rankByWorkObjectId.get(a.workObject.id);
    const bRank = rankByWorkObjectId.get(b.workObject.id);
    if (aRank !== undefined || bRank !== undefined) {
      if (aRank === undefined) {
        return 1;
      }
      if (bRank === undefined) {
        return -1;
      }
      return compareOrderKeys(aRank, bRank);
    }
    return Number(a.rank) - Number(b.rank) || a.workObject.title.localeCompare(b.workObject.title);
  });
}

function useRepairManualViewOrder({
  board,
  disabled,
  groups,
  onReplaceOrder,
  view,
}: {
  board: BoardView;
  disabled: boolean;
  groups: BoardItemGroup[];
  onReplaceOrder: (input: { boardId: string; boardViewId: string; groupKey: string; workObjectIds: string[] }) => Promise<void>;
  view: BoardView["views"][number];
}) {
  const attemptedSignaturesRef = useRef<Set<string>>(new Set());
  const repairs = useMemo(() => findManualViewOrderRepairs(view, groups), [groups, view]);
  const repairsSignature = repairs.map((repair) => `${repair.groupKey}:${repair.workObjectIds.join(",")}`).join("|");

  useEffect(() => {
    if (disabled || repairs.length === 0) {
      return;
    }

    for (const repair of repairs) {
      const signature = `${view.id}:${repair.groupKey}:${repair.workObjectIds.join(",")}`;
      if (attemptedSignaturesRef.current.has(signature)) {
        continue;
      }
      attemptedSignaturesRef.current.add(signature);
      void onReplaceOrder({
        boardId: board.id,
        boardViewId: view.id,
        groupKey: repair.groupKey,
        workObjectIds: repair.workObjectIds,
      }).catch(() => {
        attemptedSignaturesRef.current.delete(signature);
      });
    }
  }, [board.id, disabled, onReplaceOrder, repairs, repairsSignature, view.id]);
}

function findManualViewOrderRepairs(view: BoardView["views"][number], groups: BoardItemGroup[]) {
  if (normalizeViewConfig(view.config).sorts.length > 0) {
    return [];
  }

  const repairs: Array<{ groupKey: string; workObjectIds: string[] }> = [];
  const groupByKey = new Map(groups.map((group) => [group.key, group]));
  const ordersByGroup = new Map<string, BoardView["views"][number]["orders"]>();
  for (const order of view.orders) {
    ordersByGroup.set(order.groupKey, [...(ordersByGroup.get(order.groupKey) ?? []), order]);
  }

  for (const group of groups) {
    const orders = [...(ordersByGroup.get(group.key) ?? [])].sort((first, second) => compareOrderKeys(first.rank, second.rank));
    if (orders.length === 0) {
      continue;
    }
    const expectedIds = group.items.map((item) => item.workObject.id);
    const orderedIds = orders.map((order) => order.workObjectId);
    const hasDuplicateRanks = new Set(orders.map((order) => order.rank)).size !== orders.length;
    if (hasDuplicateRanks || !sameStringList(orderedIds, expectedIds)) {
      repairs.push({ groupKey: group.key, workObjectIds: expectedIds });
    }
  }

  for (const [groupKey, orders] of ordersByGroup.entries()) {
    if (!groupByKey.has(groupKey) && orders.length > 0) {
      repairs.push({ groupKey, workObjectIds: [] });
    }
  }

  return repairs;
}

function sameStringList(first: string[], second: string[]) {
  return first.length === second.length && first.every((value, index) => value === second[index]);
}

function compareOrderKeys(first: string, second: string) {
  if (first < second) {
    return -1;
  }
  if (first > second) {
    return 1;
  }
  return 0;
}

function boardItemGroupOrderSignature(groups: BoardItemGroup[]) {
  return groups.map((group) => `${group.key}:${group.items.map((item) => item.id).join(",")}`).join("|");
}

type BoardViewLayout = NonNullable<BoardViewConfig["layout"]>;

function normalizeViewLayout(layout: BoardViewConfig["layout"]): BoardViewLayout {
  return {
    cardSize: layout?.cardSize ?? "medium",
    collapseEmptyColumns: layout?.collapseEmptyColumns ?? false,
    pinFirstColumn: layout?.pinFirstColumn ?? false,
    rowSize: layout?.rowSize ?? "medium",
    showColumnColors: layout?.showColumnColors ?? true,
    showEmptyFields: layout?.showEmptyFields ?? false,
    timelineDateFieldId: layout?.timelineDateFieldId ?? null,
    wrapText: layout?.wrapText ?? false,
  };
}

function normalizeViewConfig(config: BoardViewConfig): BoardViewConfig {
  return {
    filterExpression: config.filterExpression ?? null,
    groupBy: config.groupBy ?? null,
    groupBucket: config.groupBucket ?? null,
    includeClosedItems: config.includeClosedItems ?? true,
    layout: normalizeViewLayout(config.layout),
    sorts: config.sorts ?? [],
  };
}

function visibleFieldIdsForView(view: BoardView["views"][number] | null) {
  if (!view) {
    return [];
  }
  return normalizeViewFields(view).filter((field) => field.visible).map((field) => field.fieldDefinitionId);
}

function normalizeViewFields(view: BoardView["views"][number]) {
  return [...view.fields].sort((first, second) => first.rank.localeCompare(second.rank) || first.fieldDefinitionId.localeCompare(second.fieldDefinitionId));
}

function visibleFieldIdsForItem(
  fieldIds: string[],
  fieldById: Map<string, WorkObjectFieldDefinition>,
  item: BoardItemView,
) {
  return fieldIds.filter((fieldId) => {
    const field = fieldById.get(fieldId);
    return field ? fieldAppliesToType(field, item.workObject.type?.id) : false;
  });
}

// Every board surface reads values through the shared formatter, so a relation
// field shows the person or item name here exactly as it does anywhere else,
// instead of the id it is stored as.
function fieldValueLabel(item: BoardItemView, field: WorkObjectFieldDefinition | string) {
  if (typeof field === "string") {
    return fieldValueValues(item, field).join(", ") || emptyValueLabel;
  }
  return formatFieldValue(item, field);
}

function fieldValueDraft(item: BoardItemView, fieldId: string) {
  const value = item.fieldValues.find((fieldValue) => fieldValue.fieldDefinitionId === fieldId)?.value;
  if (Array.isArray(value)) {
    return value.join("\n");
  }
  return value ?? "";
}

function isSingleLineEditableField(type: WorkObjectFieldDefinition["type"]) {
  return singleLineEditableFieldTypes.has(type);
}

function ColumnSummaryFooter({
  disabled,
  field,
  onChange,
  summary,
  summaryOperator,
}: {
  disabled: boolean;
  field: WorkObjectFieldDefinition;
  onChange: (operator: BoardViewFieldSummaryOperator | null) => void;
  summary: BoardViewField["summary"];
  summaryOperator: BoardViewField["summaryOperator"];
}) {
  const t = useT();
  const operators = summaryOperatorsForField(field);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <button
            aria-label={t("a11y.calculateField", { field: field.name })}
            className={cn(
              "inline-flex h-7 max-w-full min-w-0 items-center gap-1.5 rounded-md px-2 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50",
              summaryOperator && "text-foreground/90",
            )}
            disabled={disabled}
            type="button"
          >
            {summaryOperator && summary ? (
              <span className="flex min-w-0 items-baseline gap-1 truncate">
                <span className="shrink-0 text-[11px] text-muted-foreground/70">{summaryOperatorLabels[summary.operator]}</span>
                <span className="min-w-0 truncate font-medium tabular-nums text-foreground">
                  {formatFieldSummaryValue(field, summary.value, summary.operator)}
                </span>
              </span>
            ) : (
              <>
                <Sigma className="size-3.5 shrink-0" />
                <span className="min-w-0 truncate">{t("board.calculate")}</span>
              </>
            )}
          </button>
        }
      />
      <DropdownMenuContent align="start" className="w-44">
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => {
              onChange(null);
            }}
          >
            <span className="min-w-0 flex-1 truncate">{t("board.noCalculation")}</span>
            {!summaryOperator ? <Check className="size-4" /> : null}
          </DropdownMenuItem>
          {operators.map((operator) => (
            <DropdownMenuItem
              key={operator}
              onClick={() => {
                onChange(operator);
              }}
            >
              <span className="min-w-0 flex-1 truncate">{summaryOperatorLabels[operator]}</span>
              {summaryOperator === operator ? <Check className="size-4" /> : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function formatFieldSummaryValue(
  field: WorkObjectFieldDefinition,
  value: number | null,
  operator: BoardViewFieldSummaryOperator,
) {
  if (value === null) {
    return "-";
  }
  if (field.type === "duration" && ["sum", "avg", "min", "max"].includes(operator)) {
    return formatDurationFromSeconds(value) || "-";
  }
  if (field.type === "currency") {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(value);
  }
  if (field.type === "percent") {
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(value)}%`;
  }
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(value);
}

function calculateFieldSummaryForRows(
  field: WorkObjectFieldDefinition,
  operator: BoardViewFieldSummaryOperator,
  items: BoardItemView[],
  board: BoardView,
): NonNullable<BoardViewField["summary"]> {
  const values = items.map((item) => summaryValueForItem(item, field, board));
  const filledValues = values.filter((value) => summaryValueHasValue(value));
  const numericValues = filledValues.map(summaryNumericValue).filter((value): value is number => value !== null);
  let value: number | null = null;

  switch (operator) {
    case "count":
      value = items.length;
      break;
    case "filled":
      value = filledValues.length;
      break;
    case "empty":
      value = items.length - filledValues.length;
      break;
    case "sum":
      value = numericValues.length > 0 ? numericValues.reduce((sum, item) => sum + item, 0) : null;
      break;
    case "avg":
      value = numericValues.length > 0 ? numericValues.reduce((sum, item) => sum + item, 0) / numericValues.length : null;
      break;
    case "min":
      value = numericValues.length > 0 ? Math.min(...numericValues) : null;
      break;
    case "max":
      value = numericValues.length > 0 ? Math.max(...numericValues) : null;
      break;
  }

  return { operator, value };
}

function summaryValueForItem(item: BoardItemView, field: WorkObjectFieldDefinition, board: BoardView) {
  if (isBoardStatusField(board, field)) {
    return item.status.id;
  }
  if (field.key === "type") {
    return item.workObject.type?.id ?? "";
  }
  return item.fieldValues.find((fieldValue) => fieldValue.fieldDefinitionId === field.id)?.value ?? null;
}

function summaryValueHasValue(value: string | string[] | null) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return typeof value === "string" ? value.trim() !== "" : value !== null;
}

function summaryNumericValue(value: string | string[] | null) {
  if (Array.isArray(value)) {
    return null;
  }
  if (typeof value !== "string" || value.trim() === "") {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function summaryOperatorsForField(field: WorkObjectFieldDefinition): BoardViewFieldSummaryOperator[] {
  const operators: BoardViewFieldSummaryOperator[] = ["count", "filled", "empty"];
  if (numericFieldTypes.has(field.type)) {
    operators.push("sum", "avg", "min", "max");
  }
  return operators;
}

function readRatingScale(config: WorkObjectFieldDefinition["config"] | null | undefined) {
  const scale = typeof config?.ratingScale === "number" ? config.ratingScale : 5;
  return scale === 10 || scale === 100 ? scale : 5;
}

function readFormulaExpression(config: WorkObjectFieldDefinition["config"] | null | undefined) {
  return typeof config?.formula?.expression === "string" ? config.formula.expression : "";
}

function clampRating(value: number, scale: number) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(scale, Math.max(0, value));
}

function fieldValueLabels(item: BoardItemView, fieldId: string) {
  return fieldValueValues(item, fieldId);
}

function fieldValueValues(item: BoardItemView, fieldId: string) {
  const value = item.fieldValues.find((fieldValue) => fieldValue.fieldDefinitionId === fieldId)?.value;
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  return typeof value === "string" && value ? [value] : [];
}

function fieldOptionLabel(field: WorkObjectFieldDefinition | undefined, value: string, labels: Record<string, string> = {}) {
  return formatGroupValue(field, value, labels);
}

// Дискретные оси для группировки: варианты, люди и вычисляемые поля.
function isGroupableFieldType(type: WorkObjectFieldDefinition["type"]) {
  return type === "select" || type === "user_relation" || type === "user_multi_relation" || type === "formula" || type === "rollup";
}

function isWritableGroupBy(
  groupBy: BoardViewConfig["groupBy"],
  board: BoardView,
  fields: WorkObjectFieldDefinition[],
) {
  if (groupBy === null) {
    return true;
  }
  if (!groupBy.startsWith("field:")) {
    return false;
  }
  const fieldId = groupBy.slice("field:".length);
  const type = fields.find((field) => field.id === fieldId)?.type;
  return fieldId === board.statusFieldDefinitionId || type === "select" || type === "user_relation";
}

function fromGroupBy(groupBy: BoardViewConfig["groupBy"]) {
  return groupBy ?? "none";
}

function groupByLabel(groupBy: BoardViewConfig["groupBy"], fields: WorkObjectFieldDefinition[]) {
  if (!groupBy) {
    return "No grouping";
  }
  const fieldId = groupBy.slice("field:".length);
  return fields.find((field) => field.id === fieldId)?.name ?? "Field";
}

function sortFieldLabel(field: BoardViewConfig["sorts"][number]["field"], fields: WorkObjectFieldDefinition[]) {
  if (field === "title") {
    return "Name";
  }
  const fieldId = field.slice("field:".length);
  return fields.find((field) => field.id === fieldId)?.name ?? "Field";
}

function toGroupBy(value: string): BoardViewConfig["groupBy"] {
  return value === "none" ? null : (value as BoardViewConfig["groupBy"]);
}

function visibleFieldsForView(viewFields: BoardViewField[], fields: WorkObjectFieldDefinition[]) {
  const fieldById = new Map(fields.map((field) => [field.id, field]));
  return viewFields
    .filter((field) => field.visible)
    .map((field) => fieldById.get(field.fieldDefinitionId))
    .filter((field): field is WorkObjectFieldDefinition => Boolean(field));
}

function columnIdToFieldDefinitionId(columnId: string, visibleFields: WorkObjectFieldDefinition[]) {
  if (columnId === "type") {
    return visibleFields.find((field) => field.key === "type")?.id ?? null;
  }
  if (columnId.startsWith("field:")) {
    return columnId.slice("field:".length);
  }
  return null;
}

function statusGroupByForBoard(board: BoardView): BoardViewConfig["groupBy"] {
  return board.statusFieldDefinitionId ? `field:${board.statusFieldDefinitionId}` : null;
}

function isBoardStatusField(board: BoardView, field: WorkObjectFieldDefinition) {
  return board.statusFieldDefinitionId === field.id;
}

function isConfigurableFieldLocationScope(scopeType: string): scopeType is "workspace" | "space" | "folder" | "board" {
  return scopeType === "workspace" || scopeType === "space" || scopeType === "folder" || scopeType === "board";
}

function ensureSortModeVisible(config: BoardViewConfig, viewFields: BoardViewField[]): BoardViewConfig {
  const visibleFieldIds = new Set(viewFields.filter((field) => field.visible).map((field) => field.fieldDefinitionId));
  return {
    ...config,
    sorts: config.sorts.filter((sort) => sort.field === "title" || visibleFieldIds.has(sort.field.slice("field:".length))),
  };
}

function optimisticFieldRank(index: number) {
  const digits = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  return `a${digits[index] ?? "z"}`;
}

function uniqueViewName(views: BoardView["views"], baseName: string) {
  const existingNames = new Set(views.map((view) => view.name.toLowerCase()));
  if (!existingNames.has(baseName.toLowerCase())) {
    return baseName;
  }
  let index = 2;
  while (existingNames.has(`${baseName} ${index}`.toLowerCase())) {
    index += 1;
  }
  return `${baseName} ${index}`;
}

function selectCreatedBoard(
  nextData: BoardsView,
  input: { folderId?: string | null; name: string; spaceId: string },
  setSelectedBoardId: (boardId: string) => void,
) {
  const created = [...nextData.boards]
    .filter(
      (board) =>
        board.name === input.name.trim() &&
        board.spaceId === input.spaceId &&
        (board.folderId ?? null) === (input.folderId ?? null),
    )
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  if (created) {
    setSelectedBoardId(created.id);
  }
}
